// New Orleans, "Jazz on Jackson Square". A warm, humid October night after rain, seen from the steps of Washington
// Artillery Park on the levee side of Decatur Street, looking north-west across Decatur Street and Jackson Square at
// St Louis Cathedral (about 155 m off; we stand a few metres downriver of the square's axis, so the statue leaves the
// cathedral's door free). Left to right, as seen from there: the Upper Pontalba Building along St Peter Street, red brick
// with its two long cast-iron galleries running away from us towards Chartres Street; a glimpse up St Peter Street;
// the Cabildo with its arcade, mansard and cupola; St Louis Cathedral, white and floodlit, its three slate spires (the
// middle one tallest) and the clock in its gable; the Presbytère, the Cabildo's twin; a glimpse up St Ann Street; the
// Lower Pontalba Building along St Ann. In the square: palms, bananas and magnolias, globe lamps on the paths, and
// the general on his rearing horse in the middle, the horse facing upriver (left). In front of the square's iron
// fence: the flagstones of Decatur's pedestrian side, where a painter works at his easel, a tarot reader sits at her
// candlelit table and a jazz trio busks; the mule carriages wait at the kerb, nose to the left (upriver). Then Decatur
// Street itself, wet, with its crossing to the park, and the lowest of the park's steps under us. On the right, at the
// frame's edge, stands the corner of the beignet café beside the park, green-and-white awning, its sign on the roof
// (the city's billboard: SUGARMOON beignets, an invented name).
// The square is drawn narrower than it is (the Pontalbas stand about two thirds as far out as they would): a true
// projection of a view that holds the cathedral this large leaves the Pontalbas outside the frame. Nothing is moved
// from its side. The café stands just to the right of the park, really a little outside such a frame.
// Not in view from here: the Mississippi and the steamboat Natchez (behind us), Bourbon Street (behind the cathedral).
// render(t, { crowd }) thins the people (CROWD); crowd 0 draws nobody (see "The cast").
//
// The loop's timeline (seconds; every period divides 240):
//   4-13      a car along Decatur, left to right (cars also at 40, 160, 176, 214, 226; the far lane runs leftward)
//   8-34      a couple walks to the carriage in slot 2 and climbs in; it pulls out and leaves to the left (18-34)
//   12, 58, 106, 165   a passer-by stops and drops a coin in the trio's case
//   44-62     a pedicab with a fare, left to right
//   40-70     the moon comes out of the haze (and goes back at 150-175)
//   56-72     another carriage trots in from the right and waits in slot 2
//   66-75     BIG 1, the build-up: a tuba, three times, off to the left; the plaza turns to look
//   76-142    the second line comes down Decatur: a grand marshal with his parasol, trumpets, trombones, a sousaphone,
//             a snare and a bass drum, then dancers with parasols and handkerchiefs; the trio stops, then its trumpet
//             joins in; the photographer films; the dancer by the trio steps into the street at 110 and goes off with
//             them, and comes back along the square at 150-168
//   140-166   a family climbs into the waiting carriage; it leaves to the left
//   141       one of the café sign's lamps stutters
//   175.5-190 BIG 2: the belfry warms, the quarter chime, then the bells on the hour (180, 184, 188): the belfry
//             glows, rings widen from the tower, the photographer turns his phone on the cathedral
//   190-238   BIG 3: a couple walks in, dances to the trio in front of the gate (198-230), the plaza claps; on the
//             way off to the left he stops at the case and tips (232)
//   196-212   the first carriage comes back and pulls into slot 2
//   all loop  the drizzle comes and goes (three depths), clouds drift, the street mirrors the spire, gas lanterns
//             flicker on the galleries, windows switch on and off, October bulbs twinkle, bats loop over the square,
//             moths at the lamps, the mules shift, nod and flick ears and tails, the trio plays (the trumpet's phrase
//             every 12 s), the dancer sways and twirls every 16 s, the painter paints and steps back every 20 s, the
//             tarot reader turns a card every 6 s, walkers along the square, strollers on our side, the café's fans
//             turn, its waiter does his round every 40 s, people on the galleries and on our steps
// The longest gap between two timed things is about 6 s.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, text, textWidth, streak, hands, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= The camera and the layout =================
   Depths in metres from where we stand (d), heights above Decatur (z). The square's buildings project with one lens
   (FOC px), the horizon at HZ, our eye EYE m up on the park's steps. */
const HZ = 126, VPX = 254, FOC = 416, EYE = 6.1;
// St Louis Cathedral: its front, the base row (Chartres Street's paving) and the rows of its stages, measured on a
// frontal photo (front width = 300 units: main cornice 180, gable 220, tower 302, spire tip 458, cross 478; side
// towers 205, their spires 315, finials 342) and scaled to a 71 px front
const CA = { x0: 205, x1: 275, cx: 240, base: 142 };
const CAY = { lower: 112, cornice: 99, gable: 90, tower: 71, tip: 34, cross: 29, side: 93, sideTip: 67, fin: 61, clock: 96 };
const TOWERS = [{ x0: 205, x1: 217, cx: 211 }, { x0: 263, x1: 275, cx: 269 }];   // the side towers
const CT = { x0: 232, x1: 248 };                      // the central tower
// the Cabildo (left) and the Presbytère (right): the cathedral's width each, ridges at about a third of its height
const CAB = { x0: 131, x1: 201, cx: 166, cup: 89 }, PRE = { x0: 279, x1: 349, cx: 314, cup: 91 };
const CIV = { arcade: 127, upper: 115, ridge: 107 };  // rows: top of the arcade, the cornice, the mansard's ridge
// the Pontalba Buildings, receding: their fronts' distance out from our line of sight (m) and their far ends' columns
const PON = [{ side: -1, lat: 44.7, xFar: 124 }, { side: 1, lat: 35.4, xFar: 357 }];
const PON_FAR = 143;                                  // the far ends' depth (Chartres Street)
const STATUE = { x: 228, base: 153 };                 // the statue: the pedestal's foot
// the ground in front of the square, from far to near (rows)
const FENCE = 172;                                    // the foot of the square's iron fence along Decatur
const KERB = 189;                                     // the far kerb's top row
const PARK_Y = 199;                                   // the parked carriages' wheels touch this row
const LANE_FAR = 211, LANE_BAND = 222, LANE_NEAR = 232;   // vehicles' and the band's feet rows on Decatur
const NEAR = 242;                                     // the near kerb (the park's side of Decatur)
const STEPS = 253;                                    // the first of the park's steps under us
const GATE = { x: 210 };                              // the square's gate on Decatur, on its axis (we stand right of it)
const CAFE = { x0: 404, roof: 138 };                  // the café at the right edge: its corner column, its roof row
const BB = { x: 398, y: 104, w: 50, h: 22 };          // (its right edge stays within x 450: 16:10 screens crop the rest)          // the café's rooftop sign (the city's billboard)
const MOON = { x: 322, y: 44 };
const ACCENT = '#7f00ff';

/* ================= The loop's timeline (seconds; every period divides 240) ================= */
const CARRIAGE_X = { board: [8, 16], leave: [18, 34], back: [196, 212] };   // the carriage in slot 2 takes a couple for a ride
const CARRIAGE_Y = { come: [56, 72], board: [140, 148], leave: [150, 166] };  // another waits in slot 2 meanwhile, then leaves
const BAND = { tuba: [66, 70.5, 75], t0: 76, t1: 142 };   // the second line: the tuba first, then the band crosses
const BELLS = [180, 184, 188], CHIME = 175.5;         // the cathedral's bells on the hour (the quarter chime first)
const DANCE = [198, 230];                             // a couple dances to the trio, the plaza claps
const CARS = [                                        // [t0, t1, lane (-1 far, leftward; 1 near, rightward), colour]
  [4, 13, 1, 0], [40, 49, -1, 1], [160, 169, 1, 2], [176, 186, -1, 3], [214, 223, 1, 4], [226, 236, -1, 5]];
const PEDICAB = [44, 62];
const TIPS = [12, 58, 106, 165], DANCE_TIP = 232;     // passers-by drop a tip into the trio's case; the dancer at the end
// The share of the people the scene shows by itself: 1 = everyone, about half by default, 0 = nobody.
const CROWD = 0.5;
const rankOf = (i, off = 0) => frac((i + 0.5) * 0.6180339887 + off);

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (MIXED[k]) return MIXED[k];
  const A = C(a), B = C(b);
  return (MIXED[k] = toHex(...A.map((v, i) => Math.round(lerp(v, B[i], clamp(t, 0, 1))))));
}
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    get(i, j) { const o = (j * w + i) * 4; return [d[o], d[o + 1], d[o + 2], d[o + 3]]; },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(Math.round(x1) - Math.round(x0)), Math.abs(Math.round(y1) - Math.round(y0)), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only) */
function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** crop a painter to its opaque bounding box (build time) */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const flipC = src => { const [c, x] = makeCanvas(src.width, src.height); x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0); return c; };
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(Math.round(x), Math.round(y), w, h); };
/** piecewise-linear script: keys [[t, x], ...] sorted by t; the position at t and whether it moves */
function along(keys, t) {
  if (t <= keys[0][0]) return { x: keys[0][1], moving: false };
  for (let i = 1; i < keys.length; i++) if (t < keys[i][0]) {
    const a = keys[i - 1], b = keys[i];
    return { x: lerp(a[1], b[1], (t - a[0]) / (b[0] - a[0])), moving: a[1] !== b[1] };
  }
  return { x: keys[keys.length - 1][1], moving: false };
}

/* ================= The sky: a humid night, the city's glow under low clouds, a moon in the haze ================= */
const SKY = ['#0a0a1e', '#0e0d24', '#130f2a', '#181230', '#1e1536', '#26193c', '#2f1e42', '#3a2446', '#462a48', '#53314a',
  '#61394b', '#6f424c', '#7c4b4c'];
function renderSky() {
  const p = pix(W, 170);
  for (let y = 0; y < 170; y++) for (let x = 0; x < W; x++) {
    let v = Math.pow(y / 150, 1.35) * 0.72;
    v += 0.16 * Math.exp(-(((x - 240) / 200) ** 2)) * smooth(60, 140, y);              // the Quarter's glow under the haze
    v += 0.12 * Math.max(0, 1 - Math.hypot((x - MOON.x) / 110, (y - MOON.y) / 64));   // moonlight in the humid air
    v += (vnoise(x / 60, y / 9, 3) - 0.5) * 0.05;
    p.set(x, y, pick(SKY, v, x, y));
  }
  return p.done();
}
function renderMoon() {
  const r = 6, s = r * 2 + 1, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, d = Math.hypot(dx, dy);
    if (d > r + 0.3) continue;
    let col = dx + dy < -4 ? '#fbf1dc' : '#e6dac6';
    if (d > r - 1.1) col = dx + dy > 0 ? '#bcae9e' : '#d4c6b2';
    if (Math.hypot(dx + 2, dy - 1) < 1.5 || Math.hypot(dx - 2, dy + 2) < 1.1) col = '#c8baa6';
    p.set(i, y, col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(64);
  while (STARS.length < 22) {
    const x = Math.floor(r() * W), y = Math.floor(r() * 52);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 26 || (x > 200 && x < 282)) continue;
    STARS.push({ x, y, k: 9 + Math.floor(r() * 31), ph: r(), b: 0.2 + r() * 0.45 });
  }
}
/* low humid clouds, tileable (period W): soft banks, lit from below by the Quarter */
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 50 + r() * 90, base = h * (0.45 + r() * 0.35);
    const np = 5 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = 10 + r() * 22, ry = 3 + r() * 5;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.2, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y;
      let v = 1 - (dx * dx) / (q.rx * q.rx) - (dy * dy) / (q.ry * q.ry);
      if (y > q.base) v -= (y - q.base) * 0.35;
      if (v > best) best = v;
    }
    best += 0.15 * Math.sin(TAU * 11 * x / W + y * 0.41 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.23);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.set(x, y, col, def.a);
  }
  return { c: p.done(), y: def.y, k: def.k };
}
const CLOUD_DEFS = [
  { seed: 15, y: -6, h: 50, banks: 6, k: 1, a: 230, pal: { top: '#2a2040', body: '#211a36', core: '#1c1630', bot: '#3a2a46', bot2: '#2e2240' } },
  { seed: 33, y: 22, h: 52, banks: 5, k: 2, a: 215, pal: { top: '#3a2c4c', body: '#2e2442', core: '#28203a', bot: '#5c3e52', bot2: '#463248' } },
  { seed: 51, y: 58, h: 50, banks: 6, k: 3, a: 200, pal: { top: '#4c3854', body: '#3c2e4a', core: '#342842', bot: '#7a5258', bot2: '#5e4250' } },
];

/* ================= Windows that switch on and off, gas lanterns that flicker ================= */
const WINS = [];                                      // { x, y, w, h, mode, k, ph, duty, c1, c2 } (rects), or runs: [[x, y0, y1], …]
const LIT = [['#ffd890', '#f0b060'], ['#ffe2a8', '#e8a458'], ['#fff0c8', '#f2c27c'], ['#f6b870', '#d08040']];
function addWin(r, x, y, w, h, opt = {}) {
  const v = r(), on = opt.pOn ?? 0.4, mode = opt.mode || (v < on ? 'on' : v < on + 0.3 ? 'tog' : 'off'), lp = LIT[Math.floor(r() * r() * 4)];
  const win = { x, y, w, h, runs: opt.runs || null, mode, k: 2 + Math.floor(r() * 3), ph: r(), duty: 0.35 + r() * 0.35, c1: lp[0], c2: lp[1] };
  WINS.push(win);
  return win;
}
const winLit = (w, t) => w.mode === 'on' || (w.mode === 'tog' && frac(w.k * t / LOOP + w.ph) < w.duty);
const LANTERNS = [];                                  // gas lamps: { x, y, s (0 small .. 2 big), id }

/* ================= St Louis Cathedral: white stucco floodlit from the square, slate spires, the clock ================= */
const WHITE = ['#3e3c56', '#5c5a74', '#86829a', '#aea6b6', '#cfc4c8', '#e8ddd6', '#f8efe4', '#fffaf2'];
const SLATE = ['#17151f', '#221f2c', '#2f2b3a', '#3e384a', '#4f475a', '#62596c', '#776c7e', '#8e8292'];
const DARKW = '#1e1a28', DOOR = '#2a1a16';
/** the floodlight on the front: strongest low and in the middle, softer up the towers */
const caLight = (x, y) => 0.62 + 0.3 * smooth(60, 135, y) - 0.12 * Math.abs(x - CA.cx) / 36 + (vnoise(x / 3, y / 3, 17) - 0.5) * 0.05;
function paintCathedral(p, r) {
  const W0 = (x, y, dv = 0) => p.set(x, y, pick(WHITE, caLight(x, y) + dv, x, y, 0.6));
  // the side towers, to the top of their attic stage
  for (const T of TOWERS) for (let y = CAY.side; y < CA.base; y++) for (let x = T.x0; x <= T.x1; x++) {
    let dv = 0;
    if (x === T.x0 && T.x0 === CA.x0 || x === T.x1 && T.x1 === CA.x1) dv = -0.22;      // the outer corners turn from the light
    if (x === T.x0 + 1 || x === T.x1 - 1) dv += 0.1;                                   // corner pilasters
    W0(x, y, dv);
  }
  // the central body: two storeys, an attic over the side bays
  for (let y = 96; y < CA.base; y++) for (let x = TOWERS[0].x1 + 1; x < TOWERS[1].x0; x++) {
    let dv = 0;
    if (x === TOWERS[0].x1 + 1 || x === TOWERS[1].x0 - 1) dv = -0.3;                     // the towers stand forward: shadow
    if (y < CAY.cornice && x > 228 && x < 252) continue;                               // the gable's place
    W0(x, y, dv);
  }
  // the gable over the middle: an attic with the clock, a pediment on it
  for (let y = 89; y < CAY.cornice; y++) {
    const hw = y < 93 ? Math.round(4 + (y - 89) * 2.6) : 11;
    for (let x = CA.cx - hw; x <= CA.cx + hw; x++) W0(x, y, (y < 93 && Math.abs(x - CA.cx) >= hw - 1) ? 0.18 : 0);
  }
  // string courses and cornices: a lit band over a shadow
  const band = (y, x0, x1) => { for (let x = x0; x <= x1; x++) { W0(x, y, 0.22); W0(x, y + 1, -0.32); } };
  band(CAY.lower, CA.x0, CA.x1); band(CAY.cornice, CA.x0 - 1, CA.x1 + 1); band(95, 218, 228); band(95, 252, 262);
  for (const T of TOWERS) band(CAY.side, T.x0 - 1, T.x1 + 1);
  // paired columns on both storeys (lit face, shadow side)
  for (const x of [219, 222, 229, 232, 248, 251, 258, 261]) for (let y = CAY.cornice + 2; y < CA.base; y++) {
    if (y === CAY.lower || y === CAY.lower + 1) continue;
    W0(x, y, 0.3); W0(x + 1, y, -0.12);
  }
  const hole = (x0, x1, y0, y1, col, round = true) => {
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
      if (round && y === y0 && (x === x0 || x === x1) && x1 - x0 >= 2) continue;
      p.set(x, y, typeof col === 'function' ? col(x, y) : col);
    }
  };
  // the doors (the middle one tall and arched), the windows over them, the towers' niches and openings
  hole(236, 244, 124, 141, (x, y) => y > 138 ? '#3a2418' : x === 240 ? '#1a100e' : DOOR);
  hole(224, 227, 129, 141, DOOR); hole(253, 256, 129, 141, DOOR);
  hole(238, 242, 102, 110, (x, y) => (x === 240 ? '#3a2c3a' : y > 106 ? '#6a4a3a' : '#4a3640'));   // stained glass, faintly lit
  hole(224, 227, 103, 110, '#3a2c38'); hole(253, 256, 103, 110, '#3a2c38');
  for (const T of TOWERS) {
    hole(T.cx - 2, T.cx + 2, 118, 134, (x, y) => y < 121 ? '#3a3448' : '#2a2638');
    hole(T.cx - 1, T.cx + 1, 102, 109, DARKW);
    hole(T.cx - 1, T.cx + 1, 95, 98, '#262232', false);
  }
  for (const x of [234, 246]) { p.set(x, 129, '#fff0c8'); p.set(x, 130, '#e8b060'); }    // lamps by the main door
  // small crosses on the side bays' attic
  for (const x of [221, 259]) { for (let y = 91; y <= 94; y++) p.set(x, y, '#d8cec4'); p.set(x - 1, 92, '#bcb2aa'); p.set(x + 1, 92, '#bcb2aa'); }
  // the clock in the gable: a lit face, a dark rim (its hands are drawn per frame)
  for (let y = CAY.clock - 3; y <= CAY.clock + 3; y++) for (let x = CA.cx - 3; x <= CA.cx + 3; x++) {
    const d = Math.hypot(x - CA.cx, y - CAY.clock);
    if (d > 3.3) continue;
    p.set(x, y, d > 2.4 ? '#5a5060' : '#fff6e0');
  }
  // the central tower: corner pilasters, two louvred belfry openings, the oculus, its cornice
  for (let y = CAY.tower; y < 90; y++) for (let x = CT.x0; x <= CT.x1; x++) {
    let dv = -0.06;
    if (x === CT.x0 || x === CT.x1) dv = -0.22; else if (x === CT.x0 + 1 || x === CT.x1 - 1) dv = 0.12;
    W0(x, y, dv);
  }
  for (const x0 of [235, 243]) hole(x0, x0 + 2, 79, 88, (x, y) => (y % 2 ? '#2a2434' : '#4a4252'));
  hole(239, 241, 74, 76, '#2a2434'); p.set(240, 75, '#463e50');
  band(CAY.tower - 1, CT.x0 - 1, CT.x1 + 1);
  // the spires: octagonal slate, lit from below, three facets showing; the middle one has a lucarne a third of the way up
  const spire = (cx, tip, base, hwBase, luc) => {
    for (let y = tip; y <= base; y++) {
      const hw = hwBase * (y - tip) / (base - tip);
      for (let x = Math.round(cx - hw); x <= Math.round(cx + hw); x++) {
        const u = (x - cx) / Math.max(0.6, hw);                                          // -1 left edge .. 1 right edge
        let v = 0.32 + 0.36 * smooth(tip, base, y) + (u < -0.35 ? 0.14 : u > 0.35 ? -0.12 : 0);
        if (Math.abs(Math.abs(u) - 0.35) < 0.12 && hw > 2) v += 0.1;                      // the ridges catch the light
        p.set(x, y, pick(SLATE, v, x, y, 0.5));
      }
    }
    if (luc) {
      const [ly, lh] = luc;
      for (let y = ly; y < ly + lh; y++) for (let x = cx - 1; x <= cx + 1; x++) p.set(x, y, y === ly ? '#a89ca4' : x === cx ? '#1a1622' : '#7a6e7c');
      p.set(cx, ly - 1, '#a89ca4');
    }
  };
  spire(CA.cx, CAY.tip, CAY.tower - 2, 6.5, [57, 6]);
  for (const T of TOWERS) spire(T.cx, CAY.sideTip, CAY.side - 1, 4.4, [82, 4]);
  // the crosses on the spires
  for (let y = CAY.cross; y < CAY.tip; y++) p.set(CA.cx, y, '#cfc6bc');
  p.set(CA.cx - 1, CAY.cross + 1, '#bcb2aa'); p.set(CA.cx + 1, CAY.cross + 1, '#aaa09a');
  for (const T of TOWERS) { for (let y = CAY.fin; y < CAY.sideTip; y++) p.set(T.cx, y, '#b4aaa4'); p.set(T.cx - 1, CAY.fin + 1, '#a09690'); p.set(T.cx + 1, CAY.fin + 1, '#968c88'); }
}

/* ================= The Cabildo and the Presbytère: arcades lit from inside, mansards with dormers, cupolas ================= */
const STUCCO = ['#2e2c3e', '#45405a', '#5e566c', '#7a6e7e', '#968690', '#b09e9e', '#c8b6ae'];
const MANSARD = ['#1a1824', '#22202e', '#2c2838', '#383244', '#463e50'];
function paintCivic(p, r, B, big) {
  const bays = 9, bx = i => Math.round(B.x0 + 3.5 + i * (B.x1 - B.x0 - 7) / (bays - 1));
  const lightAt = (x, y) => 0.45 + 0.25 * smooth(110, 140, y) - (x === B.x0 || x === B.x1 ? 0.2 : 0) + (vnoise(x / 3, y / 2, 23) - 0.5) * 0.06;
  for (let y = CIV.upper; y < CA.base; y++) for (let x = B.x0; x <= B.x1; x++) {
    const pav = x >= bx(3) - 4 && x <= bx(5) + 4;                                        // the middle three bays stand forward
    p.set(x, y, pick(STUCCO, lightAt(x, y) + (pav ? 0.08 : 0) + (x === bx(3) - 4 || x === bx(5) + 4 ? -0.15 : 0), x, y, 0.6));
  }
  for (let x = B.x0; x <= B.x1; x++) {                                                  // string course, cornice
    p.set(x, CIV.arcade, pick(STUCCO, 0.85, x, CIV.arcade)); p.set(x, CIV.arcade + 1, pick(STUCCO, 0.3, x, CIV.arcade + 1));
    p.set(x, CIV.upper, pick(STUCCO, 0.9, x, CIV.upper)); p.set(x, CIV.upper + 1, pick(STUCCO, 0.25, x, CIV.upper + 1));
  }
  for (let i = 0; i < bays; i++) {
    const cx = bx(i);
    // the arcade: round arches, warm lamplight under them, a door at the back of some
    for (let y = CIV.arcade + 3; y < CA.base; y++) for (let x = cx - 2; x <= cx + 2; x++) {
      if (y === CIV.arcade + 3 && Math.abs(x - cx) === 2) continue;
      const v = smooth(CA.base, CIV.arcade + 3, y);
      let col = pick(['#5a3424', '#7a4a2c', '#a8683a', '#d8964e', '#f0bc70'], 0.25 + v * 0.7 - Math.abs(x - cx) * 0.08, x, y);
      if (i % 2 === 0 && Math.abs(x - cx) <= 1 && y > CIV.arcade + 6) col = y > CA.base - 2 ? '#3a2018' : '#4a2c1e';
      p.set(x, y, col);
    }
    // the upper storey's tall arched windows, pilasters between them
    for (let y = CIV.upper + 3; y <= CIV.arcade - 2; y++) for (let x = cx - 1; x <= cx + 1; x++) if (y > CIV.upper + 3 || x === cx) p.set(x, y, '#22202c');
    addWin(r, cx - 1, CIV.upper + 4, 3, CIV.arcade - CIV.upper - 6, { pOn: 0.25 });
    if (i < bays - 1) { const px = Math.round((cx + bx(i + 1)) / 2); for (let y = CIV.upper + 2; y < CIV.arcade; y++) p.set(px, y, pick(STUCCO, 0.75, px, y)); }
  }
  // the mansard and its dormers
  for (let y = CIV.ridge; y < CIV.upper; y++) {
    const inset = Math.round((CIV.upper - 1 - y) * 0.35);
    for (let x = B.x0 + inset; x <= B.x1 - inset; x++) p.set(x, y, y === CIV.upper - 1 ? '#4e4658' : pick(MANSARD, 0.3 + (y - CIV.ridge) * 0.06 + (x - B.x0) * 0.002, x, y));
  }
  for (let x = B.x0 + 3; x <= B.x1 - 3; x++) p.set(x, CIV.ridge, '#5a5266');
  for (let i = 0; i < bays; i++) if (i < 3 || i > 5) {
    const cx = bx(i);
    for (let y = CIV.ridge + 2; y < CIV.upper - 1; y++) for (let x = cx - 1; x <= cx + 1; x++) p.set(x, y, y === CIV.ridge + 2 ? '#8a8090' : '#6a6070');
    p.set(cx, CIV.ridge + 1, '#8a8090');
    p.set(cx, CIV.ridge + 4, '#1c1a24'); p.set(cx, CIV.ridge + 5, '#1c1a24');
    addWin(r, cx, CIV.ridge + 4, 1, 2, { pOn: 0.3 });
  }
  // the pediment over the middle bays, its raking cornice lit
  const px0 = bx(3) - 4, px1 = bx(5) + 4, pc = (px0 + px1) / 2, ph = 7;
  for (let y = CIV.upper - ph; y < CIV.upper; y++) {
    const hw = (px1 - px0) / 2 * (y - (CIV.upper - ph) + 1) / ph;
    for (let x = Math.round(pc - hw); x <= Math.round(pc + hw); x++) p.set(x, y, Math.abs(x - pc) >= hw - 1.2 ? pick(STUCCO, 0.95, x, y) : pick(STUCCO, 0.55, x, y));
  }
  p.set(Math.round(pc), CIV.upper - 3, '#d8c8b8');
  // the cupola on the ridge: a square base, an open lantern lit inside, a dome, a finial
  const cx = B.cx, top = B.cup, s = big ? 1 : 0;
  for (let y = CIV.ridge - 4; y <= CIV.ridge; y++) for (let x = cx - 3 - s; x <= cx + 3 + s; x++) p.set(x, y, pick(STUCCO, 0.6 + (x < cx ? 0.1 : -0.1), x, y));
  const l0 = top + 7, l1 = CIV.ridge - 5;
  for (let y = l0; y <= l1; y++) for (let x = cx - 3; x <= cx + 3; x++) {
    const open = (x === cx - 2 || x === cx || x === cx + 2) && y > l0;
    p.set(x, y, open ? (y < l0 + 3 ? '#ffd890' : '#e89c52') : pick(STUCCO, 0.62 + (x < cx ? 0.1 : -0.12), x, y));
  }
  for (let x = cx - 3 - s; x <= cx + 3 + s; x++) p.set(x, l0 - 1, '#c8b6ae');
  [1, 2, 2 + s, 3].forEach((hw, j) => { for (let x = cx - hw; x <= cx + hw; x++) p.set(x, top + 3 + j, pick(MANSARD, 0.6 + (x < cx ? 0.3 : 0) - j * 0.05, x, top + 3 + j)); });
  for (let y = top; y < top + 3; y++) p.set(cx, y, '#a89a90');
}
/* the alleys between them (Pirate's Alley, Père Antoine Alley) and the glimpses up St Peter and St Ann Streets */
function paintGaps(p, r) {
  for (const [x0, x1] of [[CAB.x1 + 1, CA.x0 - 1], [CA.x1 + 1, PRE.x0 - 1]]) for (let y = 116; y < CA.base; y++) for (let x = x0; x <= x1; x++)
    p.set(x, y, y > 132 ? pick(['#1a1218', '#3a2420', '#6a4028'], smooth(132, 141, y) * 0.8, x, y) : '#141220');
  for (const [x0, x1, dir] of [[PON[0].xFar + 1, CAB.x0 - 1, -1], [PRE.x1 + 1, PON[1].xFar - 1, 1]]) {
    for (let x = x0; x <= x1; x++) {
      const top = 114 + (dir < 0 ? x1 - x : x - x0);                                      // the houses up the street, lower further off
      for (let y = top; y < CA.base; y++) p.set(x, y, y === top ? '#4a3a44' : pick(['#1c1420', '#261a26', '#34222a'], 0.3 + smooth(top, CA.base, y) * 0.5, x, y));
    }
    const lx = dir < 0 ? x1 - 1 : x0 + 1;
    p.set(lx, 128, '#ffe0a0'); addWin(r, dir < 0 ? x0 + 1 : x1 - 1, 124, 1, 2, { mode: 'on' });
    LANTERNS.push({ x: lx, y: 128, s: 0, id: LANTERNS.length });
  }
}

/* ================= The Pontalba Buildings: red brick, two long cast-iron galleries, running away to Chartres Street ================= */
// Four storeys: shops, then two floors of tall French doors behind the galleries (the first on iron posts from the
// pavement, the second a balcony over it), an attic floor, the cornice, chimneys on the party walls. Ferns hang from
// the galleries, gas lanterns under them, a string of orange and purple bulbs for October on one stretch.
const BRICKP = ['#1a0a10', '#2a1016', '#3c161a', '#501e1e', '#662822', '#7c3426', '#92422c', '#a85434', '#bc6a40'];
const PZ = { shop: 4.3, deck1: 4.6, rail1: 5.6, win2: [5.2, 8.3], deck2: 8.9, rail2: 9.9, win3: [9.4, 12.2], attic: [12.9, 14.2], cornice: [14.9, 15.6], top: 15.9, chim: 18.2 };
const BAY = 3.3, SET = 2.4;                           // a bay along the front (m); how far the galleries stand out from it
const BULBS = [];                                     // the October string lights: { x, y, c }
const lace = (x, y) => ((x + y) % 3 === 0) || ((x - y + 300) % 3 === 0);   // the cast-iron lacework's pattern
function paintPontalba(p, r, P) {
  const s = P.side, x0 = s < 0 ? 0 : P.xFar, x1 = s < 0 ? P.xFar : W - 1, latG = P.lat - SET;
  const wins = new Map();
  for (let x = x0; x <= x1; x++) {
    const dx = Math.abs(x - VPX), k = dx / P.lat, d = FOC / k, kg = dx / latG;
    const along = (PON_FAR - d) / BAY, bay = Math.floor(along), u = along - bay;        // bays from the far end, towards us
    const yz = z => HZ + (EYE - z) * k, yg = z => HZ + (EYE - z) * kg;
    const farEnd = s < 0 ? x >= P.xFar - 1 : x <= P.xFar + 1;
    const chimney = ((bay % 4) + 4) % 4 === 1 && u > 0.1 && u < 0.36;
    for (let y = Math.round(yz(chimney ? PZ.chim : PZ.top)); y <= Math.round(yz(0)); y++) {
      const z = EYE - (y - HZ) / k;
      let v = 0.34 + (vnoise(x / 2, y / 1.5, 31) - 0.5) * 0.1 + ((y & 1) && hash(x >> 1, y, 32) < 0.2 ? -0.06 : 0);
      v += 0.2 * Math.exp(-(((z - 3.8) / 2) ** 2)) + 0.12 * Math.exp(-(((z - 8.2) / 1.8) ** 2));   // the lamps under the galleries
      let col = pick(BRICKP, v, x, y, 0.7);
      if (z >= PZ.top) col = z > PZ.chim - 0.35 ? '#5a4a4c' : pick(BRICKP, 0.3 + (u - 0.1) * 1.2, x, y);   // a chimney, its cap
      else if (z >= PZ.cornice[0]) col = z > PZ.cornice[1] ? '#5a4448' : z > PZ.cornice[1] - 0.3 ? '#b08a74' : pick(['#2a1c20', '#4a3634', '#6a5048'], 0.3 + (z - PZ.cornice[0]) * 1.4, x, y);
      else if (farEnd) col = pick(BRICKP, v + 0.18, x, y);
      else if (z < PZ.shop && u > 0.12 && u < 0.88) {                                   // the shops: glass lit warm, or dark
        const kind = hash(bay, s, 33);
        col = z < 0.35 ? '#2a1c1c' : z > 3.5 ? '#3a2222' : kind < 0.55 ? pick(['#a8683a', '#e0a056', '#ffd28a'], 0.3 + (z - 0.35) * 0.22, x, y) : kind < 0.75 ? pick(['#6a7088', '#b8c0d0'], 0.4 + (z - 0.35) * 0.15, x, y) : '#1e1418';
        if (Math.abs(u - 0.5) < 0.04 && z < 3.5) col = '#2a1a18';                       // the mullion / door frame
      } else {
        for (const [f, z0, z1, w] of [[2, ...PZ.win2, 0.18], [3, ...PZ.win3, 0.18], [4, ...PZ.attic, 0.12]]) {
          if (z < z0 || z >= z1) continue;
          if (Math.abs(u - 0.5) < w) {
            const key = bay * 8 + f;
            let win = wins.get(key);
            if (!win) { win = addWin(r, 0, 0, 0, 0, { runs: [], pOn: f === 4 ? 0.2 : 0.38 }); wins.set(key, win); }
            if (win.mode === 'on') col = z > (z0 + z1) / 2 ? win.c1 : win.c2;
            else col = Math.abs(u - 0.5) < 0.03 ? '#2a1a1c' : '#1a1420';
            if (win.mode === 'tog' && y === Math.round(yz(z1))) win.runs.push([x, Math.round(yz(z1)), Math.round(yz(z0))]);
          } else if (f < 4 && Math.abs(u - 0.5) < w + 0.1) col = '#1c2a24';                // open shutters, dark green
        }
      }
      p.set(x, y, col);
    }
    // the galleries' decks, their railings of lacework, the soffit of the upper one (over our eye: we see under it)
    const g = (z0, z1, fn) => { for (let y = Math.round(yg(z1)); y <= Math.round(yg(z0)); y++) fn(y); };
    for (let y = Math.round(yz(PZ.deck1)); y <= Math.round(yg(PZ.deck1)); y++) p.set(x, y, '#2a1e22');
    g(PZ.deck1 - 0.3, PZ.deck1, y => p.set(x, y, '#120c12'));
    g(PZ.deck1, PZ.rail1, y => { if (y === Math.round(yg(PZ.rail1)) || lace(x, y)) p.set(x, y, y === Math.round(yg(PZ.rail1)) ? '#3a2c30' : '#140e14'); });
    for (let y = Math.round(yg(PZ.deck2)); y <= Math.round(yz(PZ.deck2)); y++) p.set(x, y, pick(['#140c10', '#24161a', '#3a2420'], 0.3 + 0.4 * (y - yg(PZ.deck2)) / Math.max(1, yz(PZ.deck2) - yg(PZ.deck2)), x, y));
    g(PZ.deck2 - 0.25, PZ.deck2, y => p.set(x, y, '#120c12'));
    g(PZ.deck2, PZ.rail2, y => { if (y === Math.round(yg(PZ.rail2)) || lace(x, y)) p.set(x, y, y === Math.round(yg(PZ.rail2)) ? '#3a2c30' : '#140e14'); });
    // the pavement in front, wet, under the galleries
    for (let y = Math.round(yz(0)) + 1; y <= Math.round(yg(0)) + 1; y++) p.set(x, y, pick(['#1a1418', '#2a2024', '#4a3a34'], 0.3 + 0.35 * Math.exp(-(((x * 7 + y * 3) % 23) / 6)), x, y));
  }
  // the gallery posts, from the pavement up through both galleries; lanterns hung on every other one; ferns
  for (let b = 0; ; b++) {
    const dB = PON_FAR - b * BAY, kB = FOC / dB, xB = Math.round(VPX + s * latG * kB);
    if (dB < 20 || (s < 0 ? xB < -2 : xB > W + 1)) break;
    if (s < 0 ? xB > P.xFar : xB < P.xFar) continue;
    const y0 = Math.round(HZ + (EYE - PZ.rail2) * kB), y1 = Math.round(HZ + EYE * kB), wide = kB > 6.5;
    for (let y = y0; y <= y1; y++) { p.set(xB, y, '#100a10'); if (wide) p.set(xB - s, y, '#2a2026'); }
    if (b % 2 === 1) {
      const ly = Math.round(HZ + (EYE - 3.7) * kB);
      LANTERNS.push({ x: xB - s, y: ly, s: kB > 6 ? 2 : kB > 4 ? 1 : 0, id: LANTERNS.length });
      for (let y = Math.round(HZ + (EYE - PZ.deck1) * kB); y < ly - 1; y++) p.set(xB - s, y, '#1a1216');
    }
    if (b % 3 === 2) {                                                                    // a hanging fern under the upper gallery
      const fy = Math.round(HZ + (EYE - 8.2) * kB), n = kB > 5 ? 2 : 1;
      for (let j = -n; j <= n; j++) for (let i = -n; i <= n; i++) if (Math.abs(i) + Math.abs(j) <= n + 0.5) p.set(xB - s * 2 + i, fy + j, (i + j) & 1 ? '#2a4a2a' : '#3e6a36');
      p.set(xB - s * 2, fy + n + 1, '#2a4a2a');
    }
    if (b >= 5 && b <= 11) {                                                              // October bulbs along the upper rail
      const dN = dB - BAY, xN = Math.round(VPX + s * latG * FOC / dN), yA = HZ + (EYE - PZ.rail2 - 0.1) * kB, yN = HZ + (EYE - PZ.rail2 - 0.1) * FOC / dN;
      const n = Math.abs(xN - xB);
      for (let i = 1; i < n; i += 2) BULBS.push({ x: xB + Math.sign(xN - xB) * i, y: Math.round(lerp(yA, yN, i / n) + Math.sin(Math.PI * i / n) * kB * 0.25), c: (i >> 1) % 3 === 2 ? '#c070ff' : '#ff9a3a' });
    }
    if (hash(b, s, 35) < 0.3) {                                                           // a pumpkin on a shop's step
      const py = Math.round(HZ + EYE * kB), px0 = xB - s * Math.round(kB * 1.2);
      p.set(px0, py - 1, '#e07a22'); p.set(px0 + 1, py - 1, '#c8641a'); p.set(px0, py - 2, kB > 5 ? '#f0902e' : '#e07a22');
      if (kB > 5) { p.set(px0 + 1, py - 2, '#e07a22'); p.set(px0 - 1, py - 1, '#c8641a'); }
      p.set(px0, py - (kB > 5 ? 3 : 2), '#3a6a2a');
    }
  }
}

/* ================= The square: lawn, paths, palms and magnolias, globe lamps, the statue ================= */
const SQ = { left: -37.7, right: 28.4, near: 55.2, far: 138, axis: -5.9, ring: 9 };   // in metres (lateral, depth)
const groundAt = y => EYE * FOC / (y - HZ);           // the depth of the ground at row y
const latAt = (x, d) => (x - VPX) * d / FOC;
const toX = (lat, d) => Math.round(VPX + lat * FOC / d), toY = (z, d) => Math.round(HZ + (EYE - z) * FOC / d);
const SQ_LAMPS = [];                                  // globe lamps: { lat, d, x, y, base }
function placeLamps() {
  SQ_LAMPS.length = 0;
  for (const [lat, d] of [[SQ.axis - 4, 74], [SQ.axis + 4, 74], [SQ.axis - 8, 112], [SQ.axis + 8, 112], [SQ.left + 3, 100], [SQ.right - 3, 100], [SQ.left + 3, 66], [SQ.right - 3, 66], [SQ.axis - 12, 131], [SQ.axis + 12, 131]])
    SQ_LAMPS.push({ lat, d, x: toX(lat, d), y: toY(3.6, d), base: toY(0, d) });
}
function sqLight(lat, d) {
  let v = 0;
  for (const L of SQ_LAMPS) v += Math.exp(-(((lat - L.lat) / 5) ** 2) - (((d - L.d) / 7) ** 2));
  return Math.min(1, v);
}
function paintSquareGround(p) {
  for (let y = CA.base; y < FENCE + 1; y++) {
    const d = groundAt(y);
    for (let x = 0; x < W; x++) {
      const lat = latAt(x, d), L = sqLight(lat, d);
      let col;
      if (d > SQ.far || lat < SQ.left || lat > SQ.right) {                                // Chartres, St Peter, St Ann: wet flagstones
        const v = 0.3 + 0.4 * L + 0.18 * Math.exp(-(((d - 146) / 5) ** 2)) * smooth(55, 0, Math.abs(lat - SQ.axis)) + (hash(x >> 1, y, 41) - 0.5) * 0.1;
        col = pick(['#141018', '#211a22', '#33282c', '#4c3c36', '#6a5444'], v, x, y);
      } else {
        const ax = Math.abs(lat - SQ.axis), dc = Math.hypot(lat - SQ.axis, (d - 93) * 0.5);
        const diag = Math.min(Math.abs((lat - SQ.axis) - (d - 93) * 0.42), Math.abs((lat - SQ.axis) + (d - 93) * 0.42));
        const path = ax < 1.6 || Math.abs(dc - SQ.ring) < 1.2 || (diag < 1.3 && dc > SQ.ring) || Math.abs(d - SQ.near - 3) < 1 || Math.abs(d - SQ.far + 3) < 1;
        if (path) col = pick(['#2a2228', '#3e3434', '#5a4a42', '#7a6450'], 0.25 + L * 0.7 + (hash(x, y, 42) - 0.5) * 0.1, x, y);
        else if (dc < SQ.ring - 1.5) col = pick(['#1a2a1c', '#2a3a22', '#5a2a4a', '#8a3a5a'], 0.2 + hash(x >> 1, y, 43) * 0.6, x, y);   // flowerbeds round the statue
        else col = pick(['#0a120e', '#0e1a14', '#142218', '#1c2e1e', '#2a4024'], 0.2 + L * 0.65 + (vnoise(x / 4, y / 2, 44) - 0.5) * 0.15, x, y);
      }
      p.set(x, y, col);
    }
  }
}
/* trees: [kind, lateral, depth, size] — palms and bananas near the middle, magnolias and live oaks along the sides */
const TREES = [
  ['palm', -16, 120, 1.0], ['palm', 8, 118, 0.95], ['banana', -12, 108, 1], ['banana', 4, 106, 1], ['palm', -22, 100, 0.9], ['palm', 13, 98, 0.85],
  ['mag', -30, 128, 1], ['mag', 22, 126, 1], ['oak', -34, 110, 0.8], ['oak', 25, 108, 0.75], ['mag', -33, 86, 0.75], ['mag', 24, 84, 0.7],
  ['palm', -24, 72, 0.7], ['palm', 16, 70, 0.7], ['banana', -14, 66, 0.8], ['banana', 6, 64, 0.8], ['mag', -34, 64, 0.6], ['mag', 25, 62, 0.6],
];
function paintTree(p, kind, lat, d, size) {
  const k = FOC / d, x = toX(lat, d), base = toY(0, d), dk = 0.5 + 0.5 * smooth(150, 60, d);
  const leaf = (i, j, v) => p.set(i, j, pick(['#08100c', '#0e1a12', '#16261a', '#22381e', '#3a5228'], v * dk, i, j));
  if (kind === 'palm') {                                                                   // a clump of date palms: three trunks, feathery crowns
    for (const [ox, h, lean] of [[0, 6.5, 0], [-0.9, 5.2, -0.25], [1, 4.6, 0.3]]) {
      const top = base - h * size * k, tx = x + ox * k;
      for (let y = Math.round(top); y <= base; y++) { const xx = Math.round(tx + lean * (base - y) * 0.25); p.set(xx, y, (y & 1) ? '#3a2e24' : '#4a3a2a'); if (k > 4) p.set(xx + 1, y, '#2a2018'); }
      const cx = Math.round(tx + lean * (base - top) * 0.25), cy = Math.round(top), Rr = Math.max(3, Math.round(2.4 * size * k));
      for (let f = 0; f < 9; f++) {
        const a = -Math.PI / 2 + (f - 4) * 0.42, len = Rr * (0.75 + 0.3 * hash(f, x, 51));
        for (let q = 1; q <= len; q++) {
          const fx = cx + Math.cos(a) * q, fy = cy + Math.sin(a) * q * 0.6 + (q / len) ** 2 * Rr * 0.7;
          leaf(Math.round(fx), Math.round(fy), 0.35 + 0.5 * (1 - q / len) + (f < 4 ? 0.1 : 0));
          if (q > 1 && q % 2 === 0) leaf(Math.round(fx), Math.round(fy) + 1, 0.2);
        }
      }
    }
  } else if (kind === 'banana') {                                                          // broad leaves on short stems
    const h = 3.2 * size * k;
    for (let f = 0; f < 6; f++) {
      const a = -Math.PI / 2 + (f - 2.5) * 0.5, len = h * (0.7 + 0.3 * hash(f, x, 52));
      for (let q = 0; q <= len; q++) {
        const fx = x + Math.cos(a) * q * 0.8, fy = base - h * 0.35 + Math.sin(a) * q * 0.9 + (q / len) ** 2 * h * 0.3;
        for (let w = -1; w <= 1; w++) leaf(Math.round(fx + w * Math.sin(a)), Math.round(fy), 0.45 + 0.35 * (1 - q / len) + (w < 0 ? 0.15 : 0));
      }
    }
  } else {                                                                                 // magnolia / live oak: a dark rounded crown on a trunk
    const h = (kind === 'oak' ? 9 : 7) * size * k, rw = (kind === 'oak' ? 4.8 : 3) * size * k, cy = base - h * 0.62;
    for (let y = Math.round(cy); y <= base; y++) { p.set(x, y, '#1a1410'); if (k > 4) p.set(x + 1, y, '#120e0c'); }
    for (let y = Math.round(base - h); y <= Math.round(cy + h * 0.28); y++) for (let i = Math.round(x - rw); i <= Math.round(x + rw); i++) {
      const u = (i - x) / rw, v = (y - cy) / (h * 0.42), q = u * u + v * v + (vnoise(i / 2.5, y / 2, 53 + x) - 0.5) * 0.7;
      if (q > 1) continue;
      leaf(i, y, 0.25 + 0.35 * smooth(cy + h * 0.3, cy - h * 0.4, y) + 0.25 * vnoise(i / 1.6, y / 1.4, 54) + (v > 0.3 ? 0.15 : 0));
    }
  }
}
// Andrew Jackson on his rearing horse (Clark Mills, 1856), seen from the side, the horse facing upriver (left), the
// general raising his hat; bronze spotlit from below, dark against the floodlit cathedral
function paintStatue(p) {
  const { x, base } = STATUE, top = base - 17;                                             // the pedestal: plinth, die, cornice
  for (let y = top; y <= base; y++) {
    const hw = y > base - 3 ? 8 : y <= top + 1 ? 7 : 6;
    for (let i = x - hw; i <= x + hw; i++) {
      let v = 0.22 + 0.3 * smooth(top, base, y) + (i < x - 2 ? 0.1 : i > x + 3 ? -0.08 : 0);
      if (y === top || y === base - 3) v += 0.22;
      p.set(i, y, pick(['#1e1c24', '#302c36', '#46404c', '#625a66', '#867a84'], v, i, y));
    }
  }
  for (let i = x - 3; i <= x + 3; i++) p.set(i, top + 7, '#1e1c24');
  // the horse rearing on its hind legs, forelegs folded, head up and to the left; the general upright, his hat raised
  const ROWS = [
    '...HHH.............', '....H..............', '....r..rr..........', '.....r.rr..........', '......rrrr.........', '.......rrr.........',
    '.ee....rrr.........', '.nnn..rrrrr........', 'nnnnn.nrrrrr.......', 'nn.nnnnnnrrrr......', '.....nnnnnnnnn.....', '..f...nnnnnnnnnn...',
    '.fff...nnnnnnnnnnn.', '...ff..fnnnnnnnnnnn', '....f.ff.nnnnnnnnnn', '......f...nnnnnnnn.', '...........nnnnnn.t', '...........nn.nnn.t',
    '...........n...nn.t', '...........n...n..t', '..........nn...n...', '..........nn..nn...'];
  const M = new Set(), ox = x - 9, oy = top - ROWS.length;
  ROWS.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') M.add(i + ',' + j); });
  ROWS.forEach((row, j) => { for (let i = 0; i < row.length; i++) {
    const ch = row[i];
    if (ch === '.') continue;
    const below = !M.has(i + ',' + (j + 1)), above = !M.has(i + ',' + (j - 1)), broad = M.has((i - 1) + ',' + j) && M.has((i + 1) + ',' + j);
    let col = ch === 'H' ? '#3a2e24' : '#2a2018';
    if (below && broad && j > 8) col = '#94704a';                                          // the spotlights catch the undersides
    else if (above && ch !== 'H') col = '#1a1410';
    else if (ch === 'n' && !M.has((i - 1) + ',' + j) && j > 9) col = '#5a4430';
    p.set(ox + i, oy + j, col);
  } });
  for (let i = x - 11; i <= x + 11; i++) { if ((i & 1) === 0) p.set(i, base + 1, '#16121a'); p.set(i, base + 2, '#1a1418'); }   // its little fence
}
function paintSquare(p, r) {
  // the far fence along Chartres, its gate on the axis
  for (let x = toX(SQ.left, SQ.far); x <= toX(SQ.right, SQ.far); x++) { const y = toY(0, SQ.far); p.set(x, y - 1, '#1a141c'); if (x & 1) p.set(x, y - 2, '#1a141c'); p.set(x, y - 3, '#241c22'); }
  const items = [];
  for (const [kind, lat, d, size] of TREES) items.push([d, () => paintTree(p, kind, lat, d, size, r)]);
  for (const L of SQ_LAMPS) items.push([L.d, () => {
    for (let y = L.y + 1; y <= L.base; y++) p.set(L.x, y, '#141018');
    p.set(L.x - 1, L.base, '#141018'); p.set(L.x + 1, L.base, '#141018');
  }]);
  items.push([93, () => paintStatue(p)]);
  // the side fences, St Peter's and St Ann's, running towards us
  items.push([60, () => {
    for (const lat of [SQ.left, SQ.right]) for (let d = SQ.far; d >= SQ.near; d -= 0.25) {
      const x = toX(lat, d), y0 = toY(0, d), y1 = toY(1.9, d);
      if (x < 0 || x >= W) continue;
      p.set(x, y0, '#2a2024'); p.set(x, y1, '#1a141a');
      if (Math.round(d * 4) % (d > 90 ? 6 : 3) === 0) for (let y = y1; y < y0; y++) p.set(x, y, '#140e14');
    }
  }]);
  items.sort((a, b) => b[0] - a[0]);
  for (const [, fn] of items) fn();
}

/* ================= In front of the square: its fence on Decatur, the flagstones, the kerb, Decatur Street, our steps ================= */
const PLAZA_LAMPS = [{ x: 104, y: 150 }, { x: 300, y: 150 }];   // cast-iron lamp posts at the far kerb (lantern centre)
const NEAR_LAMP = { x: 24, y: 168 };                  // the lamp post at the foot of our steps
const fenceTop = 157, fenceX1 = toX(SQ.right, SQ.near);
function paintFront(p) {
  // the fence: granite base, rails, pickets with spear tips, posts; the gate on the axis, its lanterned pillars
  for (let x = 0; x <= fenceX1; x++) {
    for (let y = FENCE - 2; y <= FENCE; y++) p.set(x, y, pick(['#2a2630', '#4a4450', '#6a6070'], y === FENCE - 2 ? 0.9 : 0.4, x, y));
    p.set(x, fenceTop + 2, '#1a141c'); p.set(x, FENCE - 4, '#1a141c');
    if (x % 2 === 0) { for (let y = fenceTop + 1; y < FENCE - 2; y++) p.set(x, y, '#120e16'); p.set(x, fenceTop, (x & 2) ? '#2a2230' : '#120e16'); }
  }
  for (let x = 6; x < fenceX1; x += 42) if (Math.abs(x - GATE.x) > 14) for (let y = fenceTop - 2; y < FENCE - 2; y++) { p.set(x, y, '#3a3440'); p.set(x + 1, y, '#241e2a'); p.set(x + 2, y, '#1a141e'); }
  for (let x = GATE.x - 8; x <= GATE.x + 8; x++) for (let y = fenceTop - 3; y < FENCE - 2; y++) {   // the gate, an arched top
    const a = Math.abs(x - GATE.x), topY = fenceTop - 3 + Math.round(a * a / 22);
    if (y < topY) continue;
    if (y === topY || y === topY + 1 && a > 2 || x % 2 === 0 || y === fenceTop + 6) p.set(x, y, y === topY ? '#3a3040' : '#16101a');
    else if (p.alpha(x, y)) p.d[(y * p.w + x) * 4 + 3] = 0;                                 // see through the gate's bars
  }
  for (const gx of [GATE.x - 10, GATE.x + 9]) for (let y = fenceTop - 8; y < FENCE - 1; y++) for (let x = gx; x < gx + 2; x++) p.set(x, y, y < fenceTop - 6 ? '#5a5260' : x === gx ? '#6a6070' : '#3a3440');
  // the flagstones of the pedestrian side, wet, and the kerb
  for (let y = FENCE + 1; y < KERB; y++) {                                               // flagstones in perspective, wet
    const d = groundAt(y), dn = groundAt(y + 1), k = FOC / d, row = Math.floor(d / 1.1), hj = Math.floor(dn / 1.1) !== row;
    for (let x = 0; x < W; x++) {
      const lat = latAt(x, d), u = frac((lat + (row & 1) * 0.7) / 1.4), vj = u < 1 / (k * 1.4);
      let v = 0.24 + (hash(Math.floor((lat + (row & 1) * 0.7) / 1.4), row, 61) - 0.5) * 0.16;
      for (const L of PLAZA_LAMPS) v += 0.42 * Math.exp(-(((x - L.x) / 30) ** 2) - (((y - 184) / 9) ** 2));
      v += 0.3 * Math.exp(-(((x - GATE.x) / 26) ** 2) - (((y - 175) / 5) ** 2)) + 0.12 * smooth(FENCE, KERB, y);
      const sheen = hash(x >> 1, y, 65) < 0.06 * (1 + v) ? 0.25 : 0;                        // the wet catching the light
      p.set(x, y, hj || vj ? '#141018' : pick(['#141018', '#1e1820', '#2c2428', '#3e3230', '#5a4638', '#7a5e46'], v + sheen, x, y, 0.8));
    }
  }
  for (let x = 0; x < W; x++) { p.set(x, KERB, '#8a7c78'); p.set(x, KERB + 1, '#4a4048'); p.set(x, KERB + 2, '#1a161e'); }
  for (let x = 16; x < W; x += 30) if (x < 410) { p.set(x, KERB - 4, '#3a3440'); p.set(x, KERB - 3, '#2a2430'); p.set(x, KERB - 2, '#2a2430'); p.set(x, KERB - 1, '#2a2430'); }   // bollards
  // Decatur Street: wet asphalt, the double yellow line, the crossing from the gate to our steps
  for (let y = KERB + 3; y < NEAR; y++) {
    const d = groundAt(y), k = FOC / d, axX = VPX + SQ.axis * k;
    for (let x = 0; x < W; x++) {
      let v = 0.28 + 0.1 * (y - KERB) / (NEAR - KERB) + (vnoise(x / 9, y / 2, 62) - 0.5) * 0.12;
      let col = pick(['#0c0a12', '#14111a', '#1c1822', '#26202a', '#30282e'], v, x, y, 0.5);
      if (y === 216 || y === 218) col = x % 9 < 7 ? '#8a7a3a' : col;
      const cw = Math.abs(x - axX) < 2.1 * k, band = Math.floor((d - 23) / 0.75);
      if (cw && d > 23.5 && d < 33 && band % 2 === 0 && y !== 216 && y !== 218) col = hash(x >> 1, y, 63) < 0.2 ? '#5a565c' : '#8a8690';
      p.set(x, y, col);
    }
  }
  // the near kerb and pavement, then the park's lowest steps under us (granite, lit by our lamp)
  for (let x = 0; x < W; x++) {
    p.set(x, NEAR, '#9a8a80'); p.set(x, NEAR + 1, '#5a4c4c');
    for (let y = NEAR + 2; y < STEPS; y++) { const v = 0.3 + 0.4 * Math.exp(-(((x - NEAR_LAMP.x) / 60) ** 2)) + (hash(x >> 2, y >> 1, 64) - 0.5) * 0.1; p.set(x, y, (x + (y & 1) * 5) % 11 === 0 ? '#1a141a' : pick(['#1c161c', '#2c2226', '#40322e', '#5a463a'], v, x, y)); }
    for (let y = STEPS; y < H; y++) {
      const s0 = Math.floor((y - STEPS) / 6), j = (y - STEPS) % 6, lamp = 0.3 * Math.exp(-(((x - NEAR_LAMP.x) / 70) ** 2));
      p.set(x, y, j === 0 ? pick(['#6a5a54', '#9a8678'], 0.4 + lamp, x, y) : j === 1 ? '#1a141a' : pick(['#221a20', '#342a2c', '#4a3c38'], 0.35 + lamp + s0 * 0.05 - j * 0.03, x, y));
    }
  }
}
/* lamp posts: a cast-iron post, a lantern; h = its height in px */
function paintLampPost(p, x, y, h) {
  const big = h > 60;
  for (let yy = y + 5; yy < y + h; yy++) { p.set(x, yy, '#141018'); if (big) { p.set(x - 1, yy, '#2a2430'); p.set(x + 1, yy, '#0c0a10'); } }
  for (let yy = y + h - (big ? 6 : 3); yy < y + h; yy++) for (let i = -2; i <= 2; i++) p.set(x + i, yy, i < 0 ? '#2a2430' : '#141018');
  for (let j = -3; j <= 3; j++) {                                                           // the lantern: four panes of lit glass, a cap
    const hw = j < -2 ? 1 : j < -1 ? 2 : j > 2 ? 1 : big ? 3 : 2;
    for (let i = -hw; i <= hw; i++) p.set(x + i, y + j, Math.abs(i) === hw || j === -3 || j === 3 ? '#1a141c' : j < 0 ? '#fff4d0' : '#ffd890');
  }
  p.set(x, y - 4, '#1a141c');
}
/* ================= The beignet café at the right edge: its roof, the green-and-white awning, the lit tables ================= */
const CAFE_LAMP = { x: 452, y: 196 };
function paintCafe(p) {
  const x0 = CAFE.x0;
  for (let y = 133; y <= CAFE.roof; y++) for (let x = x0 + (CAFE.roof - y); x < W; x++) p.set(x, y, y === CAFE.roof ? '#4a4e48' : pick(['#1a1e1e', '#262a28', '#323634'], 0.4 + (y - 133) * 0.08, x, y));
  for (let y = CAFE.roof + 1; y < 162; y++) for (let x = x0; x < W; x++) p.set(x, y, pick(['#14181a', '#1e2422', '#2a302c'], 0.3 + (x - x0) * 0.01, x, y));   // the fascia, in shadow
  for (let x = x0 + 2; x < W; x += 4) { p.set(x, 159, '#3a2a20'); p.set(x, 160, (x >> 2) % 3 === 1 ? '#ffd890' : '#fff0c8'); }   // a string of bulbs under the roof
  for (let y = 162; y < 188; y++) {                                                        // the awning, seen from above: stripes, sloping out
    const out = Math.round((y - 162) * 0.5);
    for (let x = x0 - out; x < W; x++) {
      const st = Math.floor((x - x0 + out * 0.6) / 4) & 1, v = 0.45 + 0.4 * smooth(188, 162, y);
      p.set(x, y, st ? pick(['#5a6458', '#a8b0a0', '#e8ecdc'], v, x, y) : pick(['#0e3a24', '#1a5a34', '#2a7a46'], v, x, y));
    }
  }
  for (let x = x0 - 13; x < W; x++) for (let y = 188; y < 192; y++) {                      // the scalloped valance
    if (y === 191 && (x % 4 === 0)) continue;
    p.set(x, y, (Math.floor((x - x0) / 4) & 1) ? '#d8dccc' : '#1a5a34');
  }
  for (let y = 192; y < STEPS; y++) for (let x = x0 - 12; x < W; x++) {                    // under the awning: the back wall, the floor
    const v = 0.3 + 0.45 * smooth(230, 194, y) * (0.6 + 0.4 * smooth(x0 - 12, x0 + 20, x));
    let col = y < 222 ? pick(['#3a2418', '#6a4428', '#9a6a3c', '#c8925a', '#e8b878'], v + 0.1, x, y) : pick(['#1e1614', '#3a2a20', '#5a4030', '#7a5a40'], 0.35 + 0.3 * smooth(252, 226, y) + (((x >> 2) + (y >> 1)) & 1 ? 0.12 : 0), x, y);
    if (y >= 198 && y <= 208 && x >= x0 + 18 && x <= x0 + 42) col = (y === 198 || y === 208 || x === x0 + 18 || x === x0 + 42) ? '#5a3a24' : (y % 2 === 1 && x > x0 + 20 && x < x0 + 40 && hash(x >> 1, y, 66) < 0.7) ? '#d8d0b8' : '#14281c';   // the menu board
    if (y >= 214 && y <= 224 && x >= x0 + 28) col = y === 214 ? '#7a5032' : y < 216 ? '#5a3a24' : pick(['#2a1810', '#3a2418', '#4a2e1e'], 0.5 + (x % 6 === 0 ? -0.3 : 0), x, y);   // the counter
    if (y >= 206 && y < 214 && x >= x0 + 46 && x <= x0 + 50) col = x === x0 + 46 ? '#e8e8ec' : x === x0 + 50 ? '#7a7a84' : '#b8b8c0';   // the coffee urn
    p.set(x, y, col);
  }
  for (const bx of [x0 + 4, x0 + 30, x0 + 54]) { p.set(bx, 192, '#3a2a20'); p.set(bx, 193, '#fff4d0'); p.set(bx, 194, '#ffe0a0'); }   // pendant bulbs
  for (const px0 of [x0 - 12, x0 + 14, x0 + 40]) for (let y = 188; y < STEPS; y++) { p.set(px0, y, '#14301e'); p.set(px0 + 1, y, '#0e2216'); }   // its green posts
  for (const [tx, ty] of [[x0 - 4, 236], [x0 + 24, 238], [x0 + 50, 236]]) {                 // round tables, little chairs
    for (let i = -4; i <= 4; i++) p.set(tx + i, ty, '#e8e4d8');
    for (let i = -3; i <= 3; i++) p.set(tx + i, ty + 1, '#8a8478');
    for (let y = ty + 2; y < ty + 12; y++) p.set(tx, y, '#2a2420');
    for (let i = -2; i <= 2; i++) p.set(tx + i, ty + 12, '#2a2420');
  }
}
/* ================= The billboard: SUGARMOON's sign on the café roof, two gooseneck lamps over it ================= */
function renderBoard() {
  const p = pix(W, 140), L = BB.x - 2, Rr = BB.x + BB.w + 1, T0 = BB.y - 2, B = BB.y + BB.h + 1;
  for (const lx of [BB.x + 8, BB.x + BB.w - 9]) for (let y = B; y <= 136; y++) { p.set(lx, y, '#1a1c1e'); p.set(lx + 1, y, '#0e1012'); }
  p.line(BB.x + 9, B + 1, BB.x + BB.w - 10, 134, '#14161a'); p.line(BB.x + BB.w - 10, B + 1, BB.x + 9, 134, '#14161a');
  for (let y = T0; y <= B; y++) for (let x = L; x <= Rr; x++) {
    let col = '#1a201c';
    if (y === T0) col = '#5a645a'; else if (x === L || y === T0 + 1) col = '#2e3a32'; else if (y === B || x === Rr) col = '#0e120e';
    p.set(x, y, col);
  }
  for (const lx of [BB.x + 10, BB.x + BB.w - 11]) {                                        // the goosenecks
    p.set(lx, T0 - 1, '#1a1c1e'); p.set(lx, T0 - 2, '#1a1c1e'); p.set(lx + 1, T0 - 3, '#1a1c1e'); p.set(lx + 2, T0 - 3, '#1a1c1e');
    p.set(lx + 2, T0 - 2, '#3a3e3a'); p.set(lx + 3, T0 - 2, '#3a3e3a'); p.set(lx + 2, T0 - 1, '#fff0c0');
  }
  return trim(p);
}
const MOON_ICON = ['.11', '11.', '1..', '11.', '.11'];
const WIDE_M = ['10001', '11011', '10101', '10001', '10001'];  // the 3 px M reads as H or N: a wide one
const signWidth = s => [...s].reduce((w, ch) => w + (ch === 'M' ? 6 : ch === ' ' ? 2 : 4), -1);
function signText(x, s, ox, oy, col) {
  let cx = ox;
  for (const ch of s) {
    if (ch === ' ') { cx += 2; continue; }
    if (ch === 'M') { WIDE_M.forEach((row, j) => { for (let i = 0; i < 5; i++) if (row[i] === '1') { x.fillStyle = col; x.fillRect(cx + i, oy + j, 1, 1); } }); cx += 6; }
    else { text(x, ch, cx, oy, col); cx += 4; }
  }
}
function renderAd() {
  const { w, h } = BB, p = pix(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const edge = i === 0 || j === 0 || i === w - 1 || j === h - 1, inset = i === 1 || j === 1 || i === w - 2 || j === h - 2;
    p.set(i, j, edge ? '#0a2216' : inset ? '#e8ead8' : pick(['#0c2c1c', '#12382a', '#184432'], 0.5 + 0.3 * (1 - j / h), i, j));
  }
  const c = p.c, x = p.x;
  p.done();
  const l1 = 'SUGARMOON', w1 = signWidth(l1), o1 = Math.round((w - w1 - 4) / 2) + 4;
  MOON_ICON.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') { x.fillStyle = '#ffe9a0'; x.fillRect(o1 - 5 + i, 3 + j, 1, 1); } });
  signText(x, l1, o1, 3, '#fff6e2');
  const l2 = 'BEIGNETS', w2 = textWidth(l2);
  text(x, l2, Math.round((w - w2) / 2), 9, '#f6f2e6');
  const l3 = 'CAFE AU LAIT', w3 = signWidth(l3);
  signText(x, l3, Math.round((w - w3) / 2), 15, '#d8a866');
  return gradeAd(c, { level: 0.9, tint: '#1a1020', mix: 0.12, wear: 0.25, seed: 7 });
}

/* ================= Prerendered state ================= */
let MIRROR, HL, SKY_C, MOON_C, CLOUDS, BLD_C, SQUARE_C, FRONT_C, CAFE_C, BOARD_C, AD_C, PITCH, VIG_C;
const SPLIT = {};
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.48) / (H / 2) * 0.9)), q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [8, 6, 18], Math.round(q * 0.4 * 255));
  }
  return p.done();
}
function prerender() {
  for (const L of [WINS, LANTERNS, BULBS]) L.length = 0;
  placeLamps();
  HL = {
    moon: makeHalo(40, '#c8b4b8', 0.14, 1.8), moonCore: makeHalo(12, '#f0e2d0', 0.24, 1.5),
    cath: makeHalo(74, '#ffe2c0', 0.13, 1.6), cathCore: makeHalo(34, '#fff0dc', 0.12, 1.5), spire: makeHalo(18, '#ffe8cc', 0.12, 1.4),
    civic: makeHalo(40, '#ffb070', 0.1, 1.6), cupola: makeHalo(8, '#ffd890', 0.36, 1.3), clock: makeHalo(7, '#fff4d8', 0.4, 1.3),
    bell: makeHalo(36, '#ffe0a0', 0.22, 1.5), statue: makeHalo(16, '#ffd8a0', 0.14, 1.5),
    gas0: makeHalo(4, '#ffc870', 0.45, 1.2), gas1: makeHalo(7, '#ffc870', 0.4, 1.3), gas2: makeHalo(11, '#ffc070', 0.36, 1.4),
    globe: makeHalo(9, '#fff0d0', 0.34, 1.3), plaza: makeHalo(30, '#ffcf88', 0.2, 1.6), plazaCore: makeHalo(8, '#fff4d8', 0.5, 1.3),
    near: makeHalo(54, '#ffc880', 0.2, 1.7), nearCore: makeHalo(12, '#fff4d8', 0.5, 1.3), cafe: makeHalo(40, '#ffb868', 0.16, 1.6),
    sign: makeHalo(30, '#fff0c8', 0.12, 1.6), signLamp: makeHalo(4, '#fff4d0', 0.5, 1.2), candle: makeHalo(3, '#ffc060', 0.55, 1.1),
    win: makeHalo(5, '#ffb860', 0.12, 1.4), car: makeHalo(10, '#fff2d0', 0.3, 1.4), tail: makeHalo(5, '#ff3a30', 0.35, 1.2),
    lantern: makeHalo(6, '#ffc870', 0.4, 1.2), phone: makeHalo(4, '#a8d0ff', 0.4, 1.2),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  const r = rng(1718);
  { const p = pix(W, H); paintPontalba(p, r, PON[0]); paintPontalba(p, r, PON[1]); paintGaps(p, r); paintCivic(p, r, CAB, true); paintCivic(p, r, PRE, false); paintCathedral(p, r); BLD_C = trim(p); }
  { const p = pix(W, H); paintSquareGround(p); paintSquare(p, r); SQUARE_C = trim(p); }
  { const p = pix(W, H); paintFront(p); for (const L of PLAZA_LAMPS) paintLampPost(p, L.x, L.y, KERB - L.y - 1); FRONT_C = trim(p); }
  { const p = pix(W, H); paintCafe(p); paintLampPost(p, NEAR_LAMP.x, NEAR_LAMP.y, NEAR + 4 - NEAR_LAMP.y); CAFE_C = trim(p); }
  BOARD_C = renderBoard(); AD_C = renderAd();
  PITCH = makePitch(BB.w, BB.h, { accent: mixHex(ACCENT, '#ffffff', 0.35) });   // the accent, lightened: #7f00ff on the face's navy is too dark to read
  VIG_C = renderVignette();
  MIRROR = makeCanvas(W, MIR_Y1 - MIR_Y0);
  buildSprites();
}

/* ================= Per-frame: the sky, the buildings ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#f0e8ff';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  const veil = 0.6 + 0.4 * smooth(40, 70, t) * (1 - smooth(150, 175, t));                   // the moon comes out of the haze for a while
  halo(HL.moon, MOON.x, MOON.y, 0.6 + 0.4 * veil);
  ctx.globalAlpha = 0.55 + 0.35 * veil; ctx.drawImage(MOON_C, MOON.x - 6, MOON.y - 6); ctx.globalAlpha = 1;
  halo(HL.moonCore, MOON.x, MOON.y, 0.5 + 0.4 * veil);
  CLOUDS.forEach((L, i) => {
    const off = scroll(t, L.k, W);
    ctx.globalAlpha = i === 1 ? 1 - 0.6 * (veil - 0.6) : 1;
    ctx.drawImage(L.c, -off, L.y); ctx.drawImage(L.c, W - off, L.y);
  });
  ctx.globalAlpha = 1;
}
function drawWins(t) {
  for (const w of WINS) {
    if (w.mode !== 'tog' || !winLit(w, t)) continue;
    if (w.runs) { for (const [x, y0, y1] of w.runs) { const m = (y0 + y1) >> 1; R(x, y0, 1, m - y0 + 1, w.c1); if (y1 > m) R(x, m + 1, 1, y1 - m, w.c2); } }
    else if (w.h > 1) { const half = w.h >> 1; R(w.x, w.y, w.w, half, w.c1); R(w.x, w.y + half, w.w, w.h - half, w.c2); }
    else R(w.x, w.y, w.w, 1, w.c1);
  }
}
function drawLanterns(t) {
  for (const L of LANTERNS) {
    const f = 0.84 + 0.1 * wave(t, 37 + (L.id % 7) * 11, L.id * 0.13) + 0.06 * (hash(L.id, step(t, 1200), 3) - 0.5);
    if (L.s === 2) { R(L.x - 1, L.y - 2, 3, 1, '#1a1216'); R(L.x - 1, L.y - 1, 3, 3, '#ffd88a'); R(L.x, L.y, 1, 1, '#fff6d8'); R(L.x - 1, L.y + 2, 3, 1, '#1a1216'); }
    else if (L.s === 1) { R(L.x, L.y - 1, 1, 1, '#1a1216'); R(L.x, L.y, 1, 2, '#ffe0a0'); }
    else R(L.x, L.y, 1, 1, '#ffe0a0');
    halo(L.s === 2 ? HL.gas2 : L.s === 1 ? HL.gas1 : HL.gas0, L.x, L.y, f);
  }
  for (let i = 0; i < BULBS.length; i++) {
    const b = BULBS[i];
    ctx.globalAlpha = 0.7 + 0.3 * wave(t, 24, i * 0.17);
    R(b.x, b.y, 1, 1, b.c);
  }
  ctx.globalAlpha = 1;
}
const bellLevel = t => { let b = 0; for (const tb of BELLS) { const d = t - tb; if (d >= 0 && d < 4) b = Math.max(b, Math.exp(-d * 0.9) * (1 - d / 4)); } return b; };
function drawBuildings(t, clock) {
  const b = bellLevel(t), pre = smooth(CHIME - 2, CHIME + 1, t) * (1 - smooth(BELLS[2] + 2, BELLS[2] + 8, t));
  halo(HL.cath, CA.cx, 104, 0.95 + 0.05 * wave(t, 4));
  halo(HL.civic, CAB.cx, 136, 0.8); halo(HL.civic, PRE.cx, 136, 0.8);
  blit(BLD_C);
  drawWins(t);
  drawLanterns(t);
  halo(HL.cathCore, CA.cx, 104, 0.55);
  halo(HL.spire, CA.cx, 70, 0.7 + 0.12 * wave(t, 6) + b * 0.4);
  for (const B of [CAB, PRE]) halo(HL.cupola, B.cx, B.cup + 10, 0.7 + 0.1 * wave(t, 8, B.cx * 0.01));
  // the bells: the belfry warms before the hour, the strokes widen from it
  if (pre > 0.01) { ctx.globalAlpha = 0.5 * pre; R(235, 80, 3, 8, '#ffd890'); R(243, 80, 3, 8, '#ffd890'); ctx.globalAlpha = 1; halo(HL.bell, CA.cx, 84, pre * 0.6 + b); }
  for (const tb of BELLS) {
    const d = t - tb;
    if (d < 0 || d > 2.6) continue;
    const rr = 5 + d * 26, n = Math.floor(rr * 2.2);
    ctx.globalCompositeOperation = 'lighter'; ctx.fillStyle = '#ffd890'; ctx.globalAlpha = (1 - d / 2.6) * 0.4;
    for (let j = 0; j < n; j++) { const a = j / n * TAU; ctx.fillRect(Math.round(CA.cx + Math.cos(a) * rr), Math.round(84 + Math.sin(a) * rr * 0.8), 1, 1); }
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
  hands(ctx, CA.cx, CAY.clock, clock, 610, 1, 2, '#2a2230');
  halo(HL.clock, CA.cx, CAY.clock, 0.7 + b * 0.6);
}

/* ================= People =================
   One function draws everyone (after rome.js): look() gives the proportions for a height and the colours dimmed for
   the night; person() puts it on its feet. October clothes in New Orleans: short sleeves, light dresses, a straw hat.
   Their shoulders and hair catch the warm light of the lamps. */
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c', '#6a4028'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#d4d0cc'];
const NIGHT = '#140e22', GLOWC = '#ffdcae';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.22, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.37);
  return { h, hh, hw: h >= 20 ? 4 : 3, th, tw: h >= 24 ? 7 : h >= 19 ? 6 : h >= 14 ? 5 : 4, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.3), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, GLOWC, 0.35), bot: m(bot), shoe: m('#221a20'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, brim: !!o.brim, scarf: o.scarf ? m(o.scarf) : null, arm: o.sleeve ? m(SKINS[skin]) : mixHex(top, NIGHT, dk + 0.3),
    sash: o.sash ? m(o.sash) : null };
}
let LAST = null;                                      // the last person's anchors, for what they hold
/**
 * One person, feet on row fy, centred on column x. Returns the row of the top of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir: 1 right, -1 left); o.step: stride frame (profile);
 * o.sit: seated with the seat at fy; o.bend: bent forward (profile); o.lean: head px towards a neighbour;
 * o.aL / o.aR: 0 down, 1 straight up, 2 bent up to the face, 3 out, 4 up and out, 5 hand to the mouth
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll, bend = side && o.bend ? 1 : 0;
  const ty = fy - legs - th + 1 + bend, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1) + bend * dir, legC = P.skirt ? P.skin : P.bot;
  if (legs) {
    const ly = fy - legs + 1;
    ctx.fillStyle = 'rgba(10,8,20,0.35)'; ctx.fillRect(x - (bw >> 1) - 1, fy + 1, bw + 2, 1);
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
      if (P.skirt) R(x - 2, ly, 4, (legs >> 1) + 1, P.skirt);
    } else {
      const lw = bw >= 7 ? 3 : 2, sp = o.step ? 1 : 0;
      R(tx, ly, bw, 2, P.bot); R(tx - sp, ly + 2, lw, legs - 2, legC); R(tx + bw - lw + sp, ly + 2, lw, legs - 2, legC);
      R(tx - sp, fy, lw, 1, P.shoe); R(tx + bw - lw + sp, fy, lw, 1, P.shoe);
      if (P.skirt) R(tx - (bw > 5 ? 1 : 0), ly, bw + (bw > 5 ? 2 : 0), (legs >> 1) + 1, P.skirt);
    }
  } else if (o.sit && view === 1) {                    // seated facing us: the legs come down in front
    R(tx, fy + 1, 2, 4, legC); R(tx + bw - 2, fy + 1, 2, 4, legC); R(tx, fy + 5, 2, 1, P.shoe); R(tx + bw - 2, fy + 5, 2, 1, P.shoe);
    R(tx, fy - 1, bw, 2, P.skirt || P.bot);
  } else if (o.sit && side) { R(x - 1, fy - 1, 4, 2, P.skirt || P.bot); R(x + dir * 2, fy + 1, 2, 3, legC); R(x + dir * 2 - (dir < 0 ? 1 : 0), fy + 4, 3, 1, P.shoe); }
  else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.skirt || P.bot);
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD); else R(dir > 0 ? tx : tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);                                               // the lamplight on the shoulders
  if (P.sash && !side) for (let k = 0; k < th - 1; k++) R(tx + Math.min(bw - 1, k), ty + k, 1, 1, P.sash);
  else if (P.sash) R(tx, ty + 1, bw, 1, P.sash);
  if (side) {
    const st = o.aR || 0, ax = x + (o.step ? -dir : 0) + bend * dir * 2;
    if (st === 0) { R(ax, ty + 1, 1, th - 2, P.arm); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.arm); R(x + dir, hy - 3, 1, 2, P.skin); }
    else if (st === 2 || st === 5) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k) + bend * dir, ty + 2 - k, 1, 1, P.arm); R(x + dir * (st === 5 ? 2 : 4) + bend * dir, ty - (st === 5 ? 2 : 1), 1, 1, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.arm); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else { for (let k = 0; k < 3; k++) R(x + dir * (1 + k), ty + 1 - k, 1, 1, P.arm); R(x + dir * 4, ty - 2, 1, 1, P.skin); }
  } else {
    const arm = (ax, st, sg) => {
      if (!st) { R(ax, ty + 1, 1, th - 2, P.arm); R(ax, ty + th - 1, 1, 1, P.skin); }
      else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.arm); R(ax, hy - 3, 1, 2, P.skin); }
      else if (st === 2 || st === 5) { R(ax, hy + 2, 1, ty - hy, P.arm); R(ax + (st === 5 ? -sg : 0), hy + hh - 2, 1, 1, P.skin); }
      else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.arm); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
      else { for (let k = 0; k < 3; k++) R(ax + sg * k, ty + 1 - k, 1, 1, P.arm); R(ax + sg * 3, ty - 2, 1, 1, P.skin); }
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  const hx = x - (hw >> 1) + (o.lean || 0) + bend * dir * 2, hr = hh >= 5 ? 2 : 1, hY = hy + bend;
  if (view === 0) {
    R(hx, hY, hw, hh, P.hair); R(hx, hY, hw, 1, P.hairL);
    if (P.long) R(hx, hY + hh, hw, P.long, P.hair); else R(hx + 1, hY + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hY + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hY + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hY, 1, hh - 1 + P.long, P.hair); }
  if (P.scarf) { R(hx - (side ? 0 : 1), hY - 1, hw + (side ? 1 : 2), 2, P.scarf); if (!side || dir > 0) R(side ? hx : hx - 1, hY + 1, 1, hh, P.scarf); }
  if (P.hat) { R(hx, hY - 1, hw, 2, P.hat); if (P.brim) R(hx - 1, hY, hw + 2, 1, P.hat); else if (side) R(dir > 0 ? hx + hw : hx - 1, hY, 1, 1, P.hat); }
  LAST = { x, fy, tx, ty, bw, th, hx, hy: hY, hw, hh, dir, side };
  return hY;
}
const TOPS = ['#d8404a', '#e8c040', '#ece6da', '#3a6ac8', '#2a9a8a', '#e87aa8', '#e8823a', '#4a9a4a', '#8a5ac8', '#c8d0e0', '#b8323a', '#58b0d8'];
const BOTS = ['#33466e', '#22202c', '#b09a70', '#d8d4cc', '#3a3a58', '#4a3a30'];
const BRASS = '#e0b050', BRASSL = '#fff0a0', BRASSD = '#8a6428';

/* ----- what they play and carry ----- */
function trumpet(P, up) {                              // profile: from the lips forward, the bell at the end
  const { hx, hy, hw, hh, dir } = LAST, mx = dir > 0 ? hx + hw : hx - 1, my = hy + hh - 2;
  for (let k = 0; k < 5; k++) dot(mx + dir * k, my - Math.round(k * up * 0.5), k === 4 ? BRASSL : BRASS);
  dot(mx + dir * 5, my - Math.round(5 * up * 0.5) - 1, BRASS); dot(mx + dir * 5, my - Math.round(5 * up * 0.5) + 1, BRASSD); dot(mx + dir * 5, my - Math.round(5 * up * 0.5), BRASSL);
  dot(mx + dir * 2, my + 1, BRASSD);
}
function trombone(ext) {                               // profile: the slide reaching forward, the bell by the shoulder
  const { hx, hy, hw, hh, dir } = LAST, mx = dir > 0 ? hx + hw : hx - 1, my = hy + hh - 2, n = 6 + ext;
  for (let k = 0; k < n; k++) { dot(mx + dir * k, my, BRASS); if (k > 1) dot(mx + dir * k, my + 2, BRASSD); }
  dot(mx + dir * (n - 1), my + 1, BRASS);
  R(mx + dir * 2 - (dir < 0 ? 1 : 0), my - 2, 2, 3, BRASSL);
}
function sousaphone() {                                // the big white bell over the head, the brass coil round the body
  const { x, hy, ty, th, tx, bw, dir } = LAST, bx = x + dir, by = hy - 3;
  for (let k = 0; k < bw + 3; k++) { dot(tx - 2 + k, ty + th - 2 - Math.round(k * 0.6), BRASS); dot(tx - 2 + k, ty + th - 1 - Math.round(k * 0.6), BRASSD); }   // the coil round him
  R(x - dir * 2, by + 2, 1, ty + th - by - 4, BRASS);                                       // up behind his head to the bell
  ctx.fillStyle = '#e8e4dc';
  for (let j = -3; j <= 3; j++) { const hwB = Math.round(Math.sqrt(10.5 - j * j) * 0.8); ctx.fillRect(bx - hwB, by - 2 + j, hwB * 2 + 1, 1); }
  R(bx - 1, by - 3, 3, 3, '#5a5450'); R(bx + dir * 2 - (dir < 0 ? 0 : 0), by - 5, 1, 7, '#fbf8f0');
}
function snare(f) {
  const { x, ty, th, dir } = LAST, dx = x + dir * 2, dy = ty + th - 1;
  R(dx - 2, dy, 5, 1, '#f0ece4'); R(dx - 2, dy + 1, 5, 2, '#b8302a'); R(dx - 2, dy + 3, 5, 1, '#e8e4dc');
  dot(dx - 1 + f, dy - 1 - f, '#d8c8a0'); dot(dx + 2 - f, dy - 2 + f, '#d8c8a0');
}
function bassDrum(f) {
  const { x, ty, dir } = LAST, cx = x + dir * 3, cy = ty + 4;
  for (let j = -4; j <= 4; j++) { const hwD = Math.round(Math.sqrt(19 - j * j) * 0.55); R(cx - hwD, cy + j, hwD * 2 + 1, 1, '#e8e2d8'); }
  R(cx - 1, cy - 4, 3, 1, '#b8302a'); R(cx - 1, cy + 4, 3, 1, '#b8302a'); dot(cx, cy, '#8a5ac8');
  R(cx - 2, cy - 6, 5, 1, BRASS);                                                          // the cymbal on top
  dot(cx + dir * (3 + f), cy - 1 - f, '#3a2a20');
}
function parasol(t, ph, c1, c2) {                      // a second-line umbrella, decorated, twirled and pumped
  const { x, hy } = LAST, up = Math.round(1 + wave(t, 120, ph)), top = hy - 8 - up, spin = step(t, 1440) + Math.round(ph * 7);
  for (let j = 0; j < 4; j++) {
    const hwU = [2, 4, 5, 6][j];
    for (let i = -hwU; i <= hwU; i++) dot(x + i, top + j, ((i + spin + 20) >> 1) & 1 ? c1 : c2);
  }
  for (let i = -6; i <= 6; i += 2) dot(x + i, top + 4, (i + spin) & 2 ? '#fff0a0' : '#f4ecd8');   // the fringe
  R(x, top + 4, 1, hy - top - 3, '#3a2a20');
  dot(x, top - 1, '#fff0a0');
}
function hanky(t, ph, side = 1) {
  const { tx, bw, ty, hy } = LAST, ax = side > 0 ? tx + bw : tx - 1, wv = step(t, 960) + Math.round(ph * 3);
  R(ax, hy - 1, 1, ty - hy + 2, '#2a2228');
  R(ax + side * (wv & 1), hy - 4, 2, 2, '#f8f4ea'); dot(ax + side * ((wv & 1) ? 2 : -1), hy - 3, '#d8d4cc');
}
function banjo(f) {                                    // facing us: the round head at the hip, the neck up to the left shoulder
  const { tx, ty, bw, th } = LAST, cx = tx + bw - 2, cy = ty + th - 3;
  for (let j = -2; j <= 2; j++) { const hwB = j === -2 || j === 2 ? 1 : 2; R(cx - hwB, cy + j, hwB * 2 + 1, 1, Math.abs(j) === 2 ? '#8a7a68' : '#ece4cc'); }
  for (let k = 1; k <= 6; k++) dot(cx - 2 - k, cy - 2 - Math.round(k * 0.7), k === 6 ? '#3a2a1c' : '#6a4a2c');
  dot(cx + (f ? 1 : -1), cy, '#c89a78');
}
function upright(x, fy, f) {                           // the upright bass standing beside its player
  const bx = x + 6;
  for (let j = 0; j < 12; j++) { const hwB = j < 2 ? 1 : j < 5 ? 2 : j < 7 ? 2 : 3; R(bx - hwB, fy - 13 + j, hwB * 2 + 1, 1, j % 5 === 0 ? '#a8582a' : '#7a3a1e'); }
  R(bx, fy - 1, 1, 2, '#2a1a14');
  for (let y = fy - 22; y < fy - 13; y++) dot(bx, y, '#2a1a14');
  dot(bx - 1, fy - 22, '#2a1a14');
  dot(bx - 1 + f, fy - 8, '#e8d8c0'); dot(bx + 1, fy - 8, '#e8d8c0');
}

/* ================= Mule carriages: a mule in a straw hat, a lacquered open carriage, its fringed top ================= */
const COATS = [['#6a4630', '#432a1e', '#8e6448', '#3a2a24'], ['#7a6a5c', '#4e4440', '#a09080', '#4a3e3a'], ['#4a2e22', '#2e1c16', '#6e4a34', '#2a201c'], ['#8a5a36', '#5a3a24', '#b07a4c', '#4a3428']];
const LIVERY = [                                      // panel, trim, cushion, top, rims
  ['#e8e2d6', '#d8b050', '#9a2a2a', '#26242c', '#b02a24'], ['#1e1c24', '#d8b050', '#9a2a2a', '#e4ded2', '#d8b040'],
  ['#8a2222', '#d8b050', '#2a2028', '#e4ded2', '#e8e2d6'], ['#e8e2d6', '#8a5ac8', '#2a7a46', '#5a2a8a', '#2a7a46']];
const nightC = c => mixHex(c, NIGHT, 0.2);
function renderCarriageBody(v) {
  const [panel, trim, seat, top] = LIVERY[v].map(nightC), p = pix(36, 32), shade = mixHex(panel, NIGHT, 0.35);
  for (let ly = 13; ly <= 23; ly++) { p.set(0, ly, '#1a1418'); if (ly > 15) p.set(1, ly, '#2a2024'); }   // the dashboard
  for (let lx = 1; lx <= 8; lx++) p.set(lx, 23, '#2a2024');
  for (let ly = 18; ly <= 22; ly++) for (let lx = 3; lx <= 10; lx++) p.set(lx, ly, lx === 3 ? shade : panel);   // the driver's box
  for (let lx = 4; lx <= 9; lx++) { p.set(lx, 16, seat); p.set(lx, 17, mixHex(seat, NIGHT, 0.3)); }
  for (let lx = 8; lx <= 34; lx++) {                                                       // the body, its curved bottom, a gilt line
    const bot = 24 - (lx < 12 ? 12 - lx : 0) * 0.5 - (lx > 30 ? lx - 30 : 0);
    for (let ly = 17; ly <= bot; ly++) p.set(lx, ly, ly === 17 ? mixHex(panel, '#ffe0b0', 0.25) : ly === 19 ? trim : ly > bot - 1 ? shade : panel);
  }
  for (let lx = 18; lx <= 30; lx++) { p.set(lx, 15, seat); p.set(lx, 16, mixHex(seat, NIGHT, 0.3)); }
  for (let ly = 9; ly <= 17; ly++) for (let lx = 30; lx <= 34; lx++) if (!(ly === 9 && (lx === 30 || lx === 34))) p.set(lx, ly, lx === 34 ? shade : panel);
  for (let lx = 15; lx <= 35; lx++) { p.set(lx, 0, mixHex(top, '#ffe0b0', 0.2)); p.set(lx, 1, top); p.set(lx, 2, mixHex(top, NIGHT, 0.3)); if (lx & 1) p.set(lx, 3, trim); }
  for (const lx of [16, 34]) for (let ly = 3; ly <= 15; ly++) p.set(lx, ly, '#1a1418');
  for (let lx = 6; lx <= 30; lx++) p.set(lx, 25, '#1a1418');                               // the perch between the wheels
  p.set(0, 10, '#1a1418'); p.set(1, 10, '#1a1418'); p.set(0, 11, '#ffe0a0'); p.set(1, 11, '#ffd080'); p.set(0, 12, '#1a1418'); p.set(1, 12, '#1a1418');
  return p.done();
}
/** a spoked wheel; roll: how far it has rolled in pixels, + to the right. It turns roll / r radians (no slipping), and
    with y running down a growing angle is clockwise, so it turns clockwise rolling right and anticlockwise rolling left */
function wheel(cx, cy, r, rim, roll) {
  ctx.fillStyle = rim;
  const n = Math.round(r * 6.3);
  for (let i = 0; i < n; i++) { const q = i / n * TAU; ctx.fillRect(Math.round(cx + Math.cos(q) * r), Math.round(cy + Math.sin(q) * r), 1, 1); }
  ctx.fillStyle = mixHex(rim, NIGHT, 0.3);
  for (let s = 0; s < 6; s++) { const q = roll / r + s * TAU / 6; for (let k = 1; k < r; k++) ctx.fillRect(Math.round(cx + Math.cos(q) * k), Math.round(cy + Math.sin(q) * k), 1, 1); }
  R(cx - 1, cy - 1, 2, 2, '#2a2024');
}
/** a mule facing left, its front legs at ox; f: walking frame (-1 standing), nod: head down 0..2, ear / tail: small moves */
function mule(ox, fy, coat, f, nod, ear, tail, look) {
  const [B, S, L, M] = COATS[coat];
  ctx.fillStyle = 'rgba(10,8,20,0.35)'; ctx.fillRect(ox - 1, fy + 1, 20, 1);
  const leg = (x, back, lift, dx) => { const c = back ? S : B; R(x + dx, fy - 9, 2, 9 - lift, c); R(x + dx, fy - lift, 2, 1, '#1a1214'); };
  const w = f < 0 ? [0, 0, 0, 0] : [[1, 0, -1, 0], [0, 0, 0, 1], [-1, 0, 1, 0], [0, 1, 0, 0]][f];
  leg(ox + 3, true, f >= 0 && w[1] ? 1 : 0, f >= 0 ? -w[0] : 0); leg(ox + 14, true, f >= 0 && w[3] ? 1 : 0, f >= 0 ? -w[2] : 0);
  for (let j = 0; j < 8; j++) {                                                             // the body
    const y = fy - 16 + j, x0 = ox + (j === 0 ? 2 : j === 7 ? 1 : 0), x1 = ox + (j === 0 ? 14 : j === 7 ? 15 : 16);
    R(x0, y, x1 - x0 + 1, 1, j === 0 ? L : j >= 6 ? S : B);
  }
  leg(ox + 1, false, f >= 0 && w[0] > 0 ? 1 : 0, f >= 0 ? w[0] : 0); leg(ox + 12, false, f < 0 && tail > 0.6 ? 1 : f >= 0 && w[2] > 0 ? 1 : 0, f >= 0 ? w[2] : 0);
  for (let j = 0; j < 6; j++) { const sx = ox - Math.round(j * 0.7); R(sx, fy - 16 - j, 4, 1, j % 2 ? B : L); dot(sx + 3, fy - 16 - j, '#2a1c16'); }   // the neck, its mane
  const n = nod, hx = ox - 8 + (look ? 1 : 0);
  R(hx + 1, fy - 23 + n, 6, 3, B); R(hx, fy - 21 + n, 3, 3, M); R(hx + 3, fy - 20 + n, 4, 2, S); dot(hx + 4, fy - 22 + n, '#0e0a0c');
  R(hx + 5, fy - 27 + n - ear, 1, 4 + ear, L); R(hx + 6, fy - 27 + n, 1, 4, S);              // the long ears (one flicks)
  R(hx + 3, fy - 24 + n, 5, 1, '#b89858'); R(hx + 4, fy - 25 + n, 3, 1, '#c8a860'); dot(hx + 4, fy - 25 + n, '#c8508a');   // a straw hat with a flower
  R(ox + 1, fy - 17, 2, 5, '#2a1a14'); dot(ox + 2, fy - 16, BRASS);                        // the collar, its brass hame
  R(ox + 7, fy - 17, 4, 2, '#1a1216'); dot(ox + 9, fy - 17, BRASS);                        // the saddle pad
  const ts = Math.round(tail * 2) - 1;
  for (let j = 0; j < 8; j++) dot(ox + 17 + (j > 4 ? ts : 0), fy - 15 + j, '#2a1c16');
}
/** a carriage and its mule facing left; x is the mule's front legs; roll: how far it has rolled from its slot (px, + to
    the right, so negative as it drives off to the left); who: { driver, riders } */
function carriage(C, x, fy, t, moving, roll, who) {
  const v = C.livery, cx0 = x + 19, rim = nightC(LIVERY[v][4]);
  const f = moving ? step(t, 480 + C.id * 0) % 4 : -1;
  const s = step(t, 160), h = hash(C.id, s, 71);
  const nod = moving ? (f & 1) : h < 0.25 ? 2 : h < 0.45 ? 1 : 0, ear = !moving && hash(C.id, step(t, 600), 72) < 0.18 ? 1 : 0;
  const tail = moving ? 0.5 + 0.5 * wave(t, 115) : hash(C.id, step(t, 300), 73);
  wheel(cx0 + 25, fy - 7, 7, mixHex(rim, NIGHT, 0.25), roll);                               // the far side's wheels, darker
  ctx.drawImage(CARR_C[v], cx0, fy - 31);
  for (const P of who.riders) { person(cx0 + P.dx, fy - 16, P.P, { sit: true, view: 2, dir: -1, aR: P.a || 0 }); }
  mule(x, fy, C.coat, f, nod, ear, tail, C.look);
  for (let k = 0; k < 18; k++) dot(x + 2 + k, fy - 12 + Math.round(k / 18), '#5a3a22');      // the shaft
  wheel(cx0 + 5, fy - 5, 5, rim, roll); wheel(cx0 + 25, fy - 7, 7, rim, roll);
  if (who.driver) {
    person(cx0 + 7, fy - 15, who.driver, { sit: true, view: 2, dir: -1, aR: 3, lean: who.turn || 0 });
    const hx = LAST.x - 4, hy = LAST.ty + 2;
    ctx.fillStyle = '#1a1214';
    for (let k = 0; k <= 12; k++) ctx.fillRect(Math.round(lerp(hx, x - 6, k / 12)), Math.round(lerp(hy, fy - 20, k / 12) + Math.sin(Math.PI * k / 12) * 1.5), 1, 1);
  }
  halo(HL.lantern, cx0, fy - 20, 0.9);
}

/* ================= Cars on Decatur, and a pedicab ================= */
const CAR_COLS = ['#8a2a2a', '#3a4a6a', '#c8c4b8', '#2a2a30', '#5a6a5a', '#9a8a6a'];
function carSprite(w, h, col) {
  const p = pix(w, h), body = mixHex(col, NIGHT, 0.25), dark = mixHex(col, NIGHT, 0.55), lit = mixHex(col, '#ffe0b8', 0.3);
  const wr = Math.round(h * 0.2), ch = Math.round(h * 0.42), r0 = Math.round(w * 0.3), r1 = Math.round(w * 0.74);
  for (let y = 0; y < ch; y++) {                                                            // the cabin, its windows
    const a = r0 - Math.round((ch - y) * 0.9) + 2, b = r1 + Math.round((ch - y) * 0.5) - 1;
    for (let x = Math.max(1, r0 - ch + Math.round(y * 0.9)); x <= Math.min(w - 2, r1 + Math.round((ch - y) * -0.4) + ch - 2); x++) {
      const win = y > 0 && x > a && x < b && x !== Math.round((r0 + r1) / 2);
      p.set(x, y, y === 0 ? lit : win ? (y < 2 ? '#4a5a7a' : '#1a2234') : body);
    }
  }
  for (let y = ch; y < h - wr; y++) for (let x = 0; x < w; x++) {
    if ((x === 0 || x === w - 1) && y === ch) continue;
    p.set(x, y, y === ch ? lit : y > h - wr - 2 ? dark : (x === Math.round(w * 0.5) && y < h - wr - 2 ? dark : body));
  }
  for (const wx of [Math.round(w * 0.2), Math.round(w * 0.8)]) for (let y = h - 2 * wr - 1; y < h; y++) for (let x = wx - wr; x <= wx + wr; x++) {
    const d = Math.hypot(x - wx, y - (h - wr - 1));
    if (d <= wr + 0.4) p.set(x, y, d < wr * 0.45 ? '#6a6672' : '#121016');
  }
  p.set(0, ch + 1, '#fff6d8'); p.set(0, ch + 2, '#ffe6b0'); p.set(1, ch + 1, '#fff0c8');      // headlight (front = left)
  p.set(w - 1, ch + 1, '#e83a30'); p.set(w - 1, ch + 2, '#a82a24');                          // tail light
  return p.done();
}

/* ================= The cast =================
   Rank 0 (there at every crowd above 0): the jazz trio, the dancer, the painter, the tarot reader, the carriage drivers,
   the couples whose rides and dance are timed, the brass band, the café's waiter. Everyone else has a fixed rank and is
   drawn while rank < crowd. Crowd 0: nobody, and the carriages stand at the kerb with their mules. */
const L_BASS = look(18, 4, 0, '#3a4a6a', '#22202c', { hat: '#3a3440', sleeve: 1 });
const L_BANJO = look(18, 1, 2, '#e8c040', '#3a3a58', { hat: '#c8a860', brim: 1, sleeve: 1 });
const L_TRUMP = look(18, 3, 0, '#e8e4dc', '#22202c', { hat: '#2a2a34', sleeve: 1 });
const L_DANCER = look(17, 2, 0, '#e87aa8', '#e87aa8', { long: 3, skirt: '#d8508a', sleeve: 1 });
const L_PAINTER = look(17, 0, 5, '#5a7a9a', '#4a3a30', { hat: '#8a3a3a' });
const L_TAROT = look(16, 3, 0, '#5a2a7a', '#3a2040', { scarf: '#c8a040', long: 2, skirt: '#5a2a7a', sleeve: 1 });
const L_CLIENT = look(16, 0, 3, '#58b0d8', '#d8d4cc', { long: 3, sleeve: 1 });
const L_PHOTO = look(17, 1, 1, '#2a9a8a', '#33466e', { sleeve: 1 });
const DRIVERS = [look(17, 3, 0, '#e8e4dc', '#22202c', { hat: '#d8d0c0', brim: 1, sleeve: 1 }), look(17, 0, 1, '#2a2a34', '#22202c', { hat: '#2a2a34' }),
  look(17, 2, 5, '#8a2a2a', '#22202c', { hat: '#c8a860', brim: 1, sleeve: 1 }), look(17, 1, 4, '#4a9a4a', '#33466e', { hat: '#2a2a34' })];
const RIDERS_X = [{ dx: 21, P: look(15, 0, 3, '#ece6da', '#ece6da', { long: 2, sleeve: 1 }) }, { dx: 26, P: look(16, 2, 0, '#3a6ac8', '#22202c', { sleeve: 1 }) }];
const RIDERS_Y = [{ dx: 20, P: look(16, 1, 1, '#e8823a', '#33466e', { sleeve: 1 }) }, { dx: 24, P: look(12, 1, 1, '#e8c040', '#3a6ac8', { sleeve: 1, dk: 0.18 }) }, { dx: 28, P: look(16, 1, 3, '#2a9a8a', '#d8d4cc', { long: 2, sleeve: 1 }) }];
const CARRS = [
  { id: 0, slot: 10, livery: 0, coat: 0 }, { id: 1, slot: 74, livery: 1, coat: 1, script: 'X' },
  { id: 2, slot: 328, livery: 2, coat: 2 }, { id: 3, slot: 74, livery: 3, coat: 3, script: 'Y' }];   // Y waits in X's slot while X is away
const easeIn = u => u * u, easeOut = u => 1 - (1 - u) * (1 - u);
/** where a carriage is at t: x (its mule's front legs), feet row, moving, riders; null while it is away */
function carriageAt(C, t, crowd) {
  const park = { x: C.slot, fy: PARK_Y, moving: false, riders: [] };
  if (crowd === 0) return C.script === 'Y' ? null : park;
  if (C.script === 'X') {
    const [l0, l1] = CARRIAGE_X.leave, [b0, b1] = CARRIAGE_X.back;
    if (t < CARRIAGE_X.board[1]) return park;
    if (t < l0) return { ...park, riders: RIDERS_X };
    if (t < l1) { const u = (t - l0) / (l1 - l0); return { x: lerp(C.slot, -72, easeIn(u)), fy: PARK_Y + Math.round(6 * smooth(l0, l0 + 3, t)), moving: true, riders: RIDERS_X }; }
    if (t < b0) return null;
    if (t < b1) { const u = (t - b0) / (b1 - b0); return { x: lerp(W + 20, C.slot, easeOut(u)), fy: PARK_Y + Math.round(6 * (1 - smooth(b1 - 3, b1, t))), moving: u < 0.97, riders: [] }; }
    return park;
  }
  if (C.script === 'Y') {
    const [c0, c1] = CARRIAGE_Y.come, [l0, l1] = CARRIAGE_Y.leave;
    if (t < c0 || t >= l1) return null;
    if (t < c1) { const u = (t - c0) / (c1 - c0); return { x: lerp(W + 20, C.slot, easeOut(u)), fy: PARK_Y + Math.round(6 * (1 - smooth(c1 - 3, c1, t))), moving: u < 0.97, riders: [] }; }
    if (t < CARRIAGE_Y.board[1]) return park;
    if (t < l0) return { ...park, riders: RIDERS_Y };
    const u = (t - l0) / (l1 - l0);
    return { x: lerp(C.slot, -72, easeIn(u)), fy: PARK_Y + Math.round(6 * smooth(l0, l0 + 3, t)), moving: true, riders: RIDERS_Y };
  }
  return park;
}
/* the people walking along the square's pedestrian side: [lane, dir, crossings a loop, phase, looks, carry] */
const WALKERS = [
  [179, 1, 3, 0.1, [look(16, 0, 3, '#e8c040', '#d8d4cc', { long: 3, sleeve: 1 }), look(17, 1, 0, '#3a6ac8', '#22202c', { sleeve: 1 })], 'cup'],
  [188, -1, 4, 0.35, [look(17, 2, 0, '#c8d0e0', '#33466e', { hat: '#2a2a34', sleeve: 1 })], 'bag'],
  [179, -1, 2, 0.62, [look(17, 1, 2, '#4a9a4a', '#4a3a30', { sleeve: 1 }), look(12, 1, 2, '#e87aa8', '#3a6ac8', { sleeve: 1, dk: 0.18 }), look(16, 1, 3, '#e8823a', '#22202c', { long: 2, skirt: '#2a2a58', sleeve: 1 })], 'kid'],
  [188, 1, 3, 0.8, [look(17, 3, 0, '#b8323a', '#22202c', { sleeve: 1 })], 'cup'],
  [179, 1, 4, 0.48, [look(16, 0, 1, '#8a5ac8', '#d8d4cc', { long: 4, skirt: '#8a5ac8', sleeve: 1 })], 'bag'],
  [188, -1, 3, 0.05, [look(17, 0, 4, '#ece6da', '#b09a70', { hat: '#c8a860', brim: 1, sleeve: 1 }), look(16, 2, 1, '#58b0d8', '#33466e', { long: 2, sleeve: 1 })], 'cup'],
].map(([lane, dir, k, ph, P, carry], i) => ({ lane, dir, k, ph, P, carry, rank: rankOf(i) }));
const TIPPERS = TIPS.map((tt, i) => ({ t: tt, from: i % 2 ? 1 : -1, P: look(17, (i * 3) % 5, (i * 2) % 6, TOPS[(i * 5 + 2) % 12], BOTS[i % 6], { sleeve: 1, long: i % 2 ? 2 : 0 }), rank: rankOf(i, 0.21) }));
const TRIO = { bass: 131, banjo: 142, trump: 154, case: 145 };
const DANCER_X = 168, PAINT = { easel: 268, x: 276 }, TAROT = { table: 247, reader: 256, client: 238 }, PHOTO_X = 302;
const L_HE = look(18, 4, 0, '#ece6da', '#2a2a34', { hat: '#2a2a34', sleeve: 1 }), L_SHE = look(17, 1, 3, '#e8c040', '#e8c040', { long: 3, skirt: '#d84a3a', sleeve: 1 });
const BOARD_X = [{ P: RIDERS_X[0].P, dx: 0 }, { P: RIDERS_X[1].P, dx: 7 }], BOARD_Y = RIDERS_Y.map((r, i) => ({ P: r.P, dx: i * 7 }));
const inBand = t => t >= BAND.t0 && t < BAND.t1;
const inDance = t => t >= DANCE[0] && t < DANCE[1];
/** the trio, their case, the coins that land in it */
function drawTrio(t, crowd) {
  const rest = t >= BAND.t0 + 2 && t < BAND.t0 + 16, join = t >= BAND.t0 + 16 && t < BAND.t1 - 12, live = inDance(t);
  const beat = step(t, 960) & 1, beat2 = step(t, 480) & 1, sway = Math.round(wave(t, 120) * 0.6 + (live ? wave(t, 240) * 0.6 : 0));
  // the open case in front, red lining, coins
  R(TRIO.case - 4, 186, 9, 2, '#1a1214'); R(TRIO.case - 3, 186, 7, 1, '#8a2a3a');
  TIPPERS.forEach(T => { if (T.rank < crowd && (t - T.t + LOOP) % LOOP < 50) dot(TRIO.case - 2 + (T.t % 5), 186, '#e8c860'); });   // each coin glints a while
  if ((t - DANCE_TIP + LOOP) % LOOP < 50) dot(TRIO.case + 2, 186, '#e8c860');
  person(TRIO.bass, 186, L_BASS, { view: 1, aL: rest ? 0 : 3, aR: rest ? 0 : 3, lean: rest ? 0 : sway });
  upright(TRIO.bass, 186, rest ? 0 : beat2);
  person(TRIO.banjo, 186, L_BANJO, { view: rest ? 2 : 1, dir: -1, aL: rest ? 0 : 3, aR: 0, lean: rest ? 0 : -sway });
  if (!rest) banjo(beat); else dot(LAST.x + 2, LAST.ty + 4, '#ece4cc');
  const phrase = live || join || frac(t / 12) < 0.55;
  if (rest) { person(TRIO.trump, 186, L_TRUMP, { view: 2, dir: -1, aR: 0 }); dot(LAST.x - 1, LAST.ty + LAST.th, BRASS); }
  else if (join) { person(TRIO.trump, 186, L_TRUMP, { view: 2, dir: -1, aR: 3, lean: beat2 }); trumpet(L_TRUMP, 2); }
  else { person(TRIO.trump, 186, L_TRUMP, { view: 2, dir: 1, aR: phrase ? 3 : 0, bend: !phrase && beat2 === 0 && frac(t / 12) > 0.8 }); if (phrase) trumpet(L_TRUMP, live ? 2 : 1); else dot(LAST.x + 1, LAST.ty + LAST.th, BRASS); }
}
/** the dancer: sways and twirls by the trio; goes off with the second line, comes back along the square */
function dancerAt(t) {
  const s0 = BAND.t0 + 34, s1 = BAND.t0 + 44, back0 = BAND.t1 + 8, back1 = BAND.t1 + 26;
  if (t >= s0 && t < s1) { const u = (t - s0) / (s1 - s0); return { x: lerp(DANCER_X, 300, u), fy: Math.round(lerp(186, LANE_BAND + 3, smooth(0, 1, u))), walk: 1, dir: 1 }; }
  if (t >= s1 && t < BAND.t1) return { x: 300 + (t - s1) * 9.6, fy: LANE_BAND + 3, walk: 2, dir: 1 };
  if (t >= BAND.t1 && t < back0) return null;
  if (t >= back0 && t < back1) return { x: lerp(W + 12, DANCER_X, (t - back0) / (back1 - back0)), fy: 179, walk: 1, dir: -1 };
  return { x: DANCER_X, fy: 186, walk: 0, dir: 1 };
}
function drawDancer(t, D) {
  const c = frac(t / 16), beat = step(t, 480) & 1;
  if (D.walk === 1) { person(D.x, D.fy, L_DANCER, { view: 2, dir: D.dir, step: Math.floor(D.x / 3) & 1 }); return; }
  if (D.walk === 2) { person(D.x, D.fy - beat, L_DANCER, { view: 2, dir: 1, step: beat, aR: 1 }); hanky(t, 0.3); return; }
  const live = inDance(t) || inBand(t);
  if (c > 0.4 && c < 0.52) { const v = [1, 2, 0, 2][step(t, 960) & 3]; person(D.x, D.fy, L_DANCER, { view: v, dir: v === 2 && (step(t, 960) & 3) === 3 ? -1 : 1, aL: 3, aR: 1 }); return; }
  const sw = Math.round(wave(t, 120) * (live ? 1.4 : 0.8));
  person(D.x + sw, D.fy, L_DANCER, { view: 1, aL: live ? (beat ? 1 : 3) : c < 0.4 ? 3 : 0, aR: live ? (beat ? 3 : 1) : c < 0.4 ? 3 : 0, step: live && beat });
}
function drawPainter(t, crowd) {
  const c = frac(t / 20 + 0.3), back = c > 0.78, look = (t >= BAND.tuba[0] && t < BAND.t1) || (t > BELLS[0] - 4 && t < BELLS[2] + 3);
  R(PAINT.easel - 3, 168, 7, 8, '#1a141a');                                                // the easel and his canvas
  R(PAINT.easel - 2, 169, 5, 6, '#3a5a7a'); R(PAINT.easel - 2, 172, 5, 3, '#c87a3a'); dot(PAINT.easel, 170, '#f0e0a0');
  for (let y = 176; y <= 186; y++) { dot(PAINT.easel - 2 - ((y - 176) >> 2), y, '#3a2a20'); dot(PAINT.easel + 2 + ((y - 176) >> 2), y, '#3a2a20'); }
  if (crowd === 0) return;
  const x = back ? PAINT.x + 3 : PAINT.x;
  person(x, 186, L_PAINTER, { view: 2, dir: -1, aR: look || back ? 0 : (step(t, 600) & 1) ? 3 : 2 });
  if (!look && !back) dot(x - 5, LAST.ty + 2, '#f0e0a0');
}
function drawTarot(t, crowd) {
  const flip = step(t, 40), lift = frac(t / 6) < 0.25;
  R(TAROT.table - 5, 179, 11, 1, '#7a3a9a'); R(TAROT.table - 5, 180, 11, 6, '#4a2066');    // the table under a purple cloth
  for (let i = -5; i <= 5; i += 2) dot(TAROT.table + i, 186, '#c8a040');
  for (let i = 0; i < 3; i++) { const up = (flip + i) % 3 === 0; R(TAROT.table - 4 + i * 3, 178, 2, 1, up ? '#f4ecd8' : '#8a2a3a'); }
  R(TAROT.table + 3, 175, 2, 3, '#2a2028'); dot(TAROT.table + 3, 176, '#ffe0a0'); dot(TAROT.table + 4, 176, '#ffc870');   // the candle lantern
  if (crowd === 0) return;
  person(TAROT.reader, 183, L_TAROT, { sit: true, view: 2, dir: -1, aR: lift ? 3 : 0 });
  if (crowd > 0.35) person(TAROT.client, 183, L_CLIENT, { sit: true, view: 2, dir: 1, aR: frac(t / 10) < 0.2 ? 5 : 0, lean: frac(t / 10 + 0.5) < 0.3 ? 1 : 0 });
}
function drawPhoto(t) {
  const shoot = frac(t / 24 + 0.1) < 0.35, band = t >= BAND.t0 + 18 && t < BAND.t0 + 40, bells = t >= CHIME - 2 && t < BELLS[2] + 4, clap = inDance(t);
  if (clap) { const b = step(t, 960) & 1; person(PHOTO_X, 185, L_PHOTO, { view: 2, dir: -1, aR: b ? 3 : 2 }); return; }
  if (band) { person(PHOTO_X, 185, L_PHOTO, { view: 1, aR: 2, aL: 2 }); R(LAST.hx, LAST.hy - 2, 3, 2, '#0e1018'); return; }
  if (t >= BAND.tuba[0] && t < BAND.t1) { person(PHOTO_X, 185, L_PHOTO, { view: 2, dir: -1, aR: 0 }); return; }   // he turns to the sound
  person(PHOTO_X, 185, L_PHOTO, { view: 0, aR: shoot || bells ? 2 : 0 });
  if (shoot || bells) { R(LAST.hx + 1, LAST.hy - 3, 3, 2, '#9ad0ff'); halo(HL.phone, LAST.hx + 2, LAST.hy - 2, 0.8); }
}
function drawWalker(Wk, t) {
  const u = frac(Wk.k * t / LOOP + Wk.ph), x0 = Wk.dir > 0 ? lerp(-30, W + 30, u) : lerp(W + 30, -30, u);
  Wk.P.forEach((P, j) => {
    const x = Math.round(x0 - Wk.dir * j * 7), st = (Math.floor(x / 3) + j) & 1;
    person(x, Wk.lane + (j === 1 && Wk.carry === 'kid' ? 0 : 0), P, { view: 2, dir: Wk.dir, step: st, aR: j === 0 && Wk.carry === 'cup' ? 3 : 0 });
    if (j === 0 && Wk.carry === 'cup') { R(x + Wk.dir * 4, LAST.ty + 1, 1, 2, '#e8e4d8'); if (step(t, 240) & 1) dot(x + Wk.dir * 4, LAST.ty - 1, '#c8c4cc'); }
    if (j === 0 && Wk.carry === 'bag') R(x - Wk.dir * 1, LAST.ty + LAST.th - 1, 3, 3, '#f0ece0');   // a paper bag of beignets
  });
}
function tipperAt(T, t) {
  if (t < T.t - 10 || t >= T.t + 10) return null;
  const s = along([[T.t - 10, TRIO.case + T.from * 160], [T.t - 1.5, TRIO.case + T.from * 4], [T.t + 1.5, TRIO.case + T.from * 4], [T.t + 10, TRIO.case - T.from * 160]], t);
  return { x: s.x, moving: s.moving, dir: s.moving ? -T.from : T.from < 0 ? 1 : -1, bend: !s.moving && Math.abs(t - T.t) < 0.8 };
}
function drawBoarders(t) {
  const out = [];
  const go = (list, [b0, b1], from, to) => {
    if (t < b0 || t >= b1) return;
    const u = clamp((t - b0) / (b1 - b0 - 1.5), 0, 1);
    list.forEach(B => out.push([188, () => person(lerp(from, to, u) + B.dx, 188, B.P, { view: 2, dir: to < from ? -1 : 1, step: u < 1 && (Math.floor(lerp(from, to, u) / 3) & 1) })]));
  };
  go(BOARD_X, CARRIAGE_X.board, 150, CARRS[1].slot + 36);
  go(BOARD_Y, CARRIAGE_Y.board, 150, CARRS[3].slot + 36);
  return out;
}
function drawDanceCouple(t) {
  const [d0, d1] = DANCE;
  if (t < d0 - 8 || t >= d1 + 8) return;
  if (t < d0) { const u = (t - d0 + 8) / 8, x = lerp(330, 184, u); person(x, 188, L_SHE, { view: 2, dir: -1, step: Math.floor(x / 3) & 1 }); person(x + 8, 188, L_HE, { view: 2, dir: -1, step: (Math.floor(x / 3) + 1) & 1 }); return; }
  if (t >= d1) {                                                                            // off to the left, a tip on the way
    const s = along([[d1, 184], [DANCE_TIP - 0.3, TRIO.case - 4], [DANCE_TIP + 1.2, TRIO.case - 4], [d1 + 8, -24]], t), x = s.x, bend = !s.moving;
    person(x, 188, L_SHE, { view: 2, dir: -1, step: s.moving && (Math.floor(x / 3) & 1) });
    person(x + 8, 188, L_HE, { view: 2, dir: -1, step: s.moving && ((Math.floor(x / 3) + 1) & 1), bend, aR: bend ? 3 : 0 });
    if (bend && t > DANCE_TIP) dot(TRIO.case + 2, 186 - (t < DANCE_TIP + 0.5 ? 1 : 0), '#ffe890');
    return;
  }
  const c = frac((t - d0) / 8), beat = step(t, 480) & 1;
  if (c > 0.55 && c < 0.75) {                                                               // she twirls under his raised arm
    const v = [1, 2, 0, 2][step(t, 960) & 3];
    person(186, 188, L_SHE, { view: v, dir: (step(t, 960) & 3) === 3 ? -1 : 1, aR: 1 });
    person(193, 188, L_HE, { view: 2, dir: -1, aR: 1 });
  } else {
    const off = Math.round(wave(t, 240) * 1.5);
    person(184 - off, 188 - beat, L_SHE, { view: 2, dir: 1, aR: 3, step: beat });
    person(193 + off, 188 - (beat ^ 1), L_HE, { view: 2, dir: -1, aR: 3, step: beat ^ 1 });
  }
}
/* ================= The second line: a brass band down Decatur, a grand marshal, parasols and handkerchiefs ================= */
const BRASS_LOOK = (s, hat = '#1e1c24') => look(25, s, 0, '#ece8e0', '#1e1c24', { hat, sleeve: 0, dk: 0.24 });
const BAND_M = [
  [0, 0, look(25, 3, 0, '#ece8e0', '#1e1c24', { sash: '#8a5ac8', hat: '#ece8e0', brim: 1, dk: 0.22 }), 'marshal', '#8a5ac8', '#e8c040'],
  [-15, -2, BRASS_LOOK(4), 'trumpet'], [-19, 3, BRASS_LOOK(3), 'trumpet'],
  [-31, -1, BRASS_LOOK(2), 'trombone'], [-35, 3, BRASS_LOOK(4), 'trombone'],
  [-49, 0, BRASS_LOOK(3), 'tuba'],
  [-63, -2, BRASS_LOOK(1), 'snare'], [-68, 3, BRASS_LOOK(4), 'bass'],
  [-84, 0, look(24, 2, 0, '#e8823a', '#3a3a58', { sleeve: 1, hat: '#e8c040', brim: 1 }), 'parasol', '#2a8a4a', '#e8c040'],
  [-94, 3, look(24, 1, 3, '#e87aa8', '#e87aa8', { long: 3, skirt: '#c83a6a', sleeve: 1 }), 'hanky'],
  [-104, -1, look(24, 4, 0, '#58b0d8', '#22202c', { sleeve: 1 }), 'parasol', '#8a5ac8', '#f4ecd8'],
  [-115, 2, look(23, 0, 4, '#e8c040', '#33466e', { long: 2, sleeve: 1 }), 'hanky'],
  [-126, 0, look(24, 3, 1, '#b8323a', '#e8e4dc', { long: 4, skirt: '#e8e4dc', sleeve: 1 }), 'parasol', '#d84a3a', '#e8c040'],
];
function bandLead(t) { return lerp(-24, W + 150, (t - BAND.t0) / (BAND.t1 - BAND.t0)); }
function bandItems(t) {
  if (!inBand(t)) return [];
  const lead = bandLead(t), beat = step(t, 480);
  return BAND_M.map(([dx, dy, P, role, c1, c2], i) => [LANE_BAND + dy, () => {
    const x = lead + dx, b = (beat + i) & 1, fy = LANE_BAND + dy - b;
    if (x < -14 || x > W + 14) return;
    const aR = role === 'marshal' || role === 'parasol' ? 1 : role === 'hanky' ? 0 : role === 'tuba' || role === 'snare' || role === 'bass' ? 0 : 3;
    person(x, fy, P, { view: 2, dir: 1, step: b, aR });
    if (role === 'trumpet') trumpet(P, frac(t / 8 + i * 0.1) < 0.6 ? 2 : 0);
    else if (role === 'trombone') trombone(Math.round(2 + 2 * wave(t, 240, i * 0.3)));
    else if (role === 'tuba') sousaphone();
    else if (role === 'snare') snare(beat & 1);
    else if (role === 'bass') bassDrum(beat & 1);
    else if (role === 'hanky') hanky(t, i * 0.13);
    else parasol(t, i * 0.17, c1, c2);
  }]);
}

/* ----- on our steps, at the café ----- */
const SITTERS = [
  [{ x: 70, P: look(27, 1, 3, '#e8823a', '#33466e', { long: 4, sleeve: 1, dk: 0.3 }), act: 'eat' }, { x: 81, P: look(28, 3, 0, '#3a6ac8', '#22202c', { sleeve: 1, dk: 0.3 }), act: 'eat2' }],
  [{ x: 300, P: look(28, 0, 1, '#4a9a4a', '#4a3a30', { hat: '#c8a860', brim: 1, sleeve: 1, dk: 0.3 }), act: 'coffee' }],
  [{ x: 362, P: look(27, 2, 4, '#e87aa8', '#22202c', { long: 3, sleeve: 1, dk: 0.3 }), act: 'hanky' }],
].map((g, i) => ({ g, rank: rankOf(i, 0.4) }));
const PATRONS = [
  [{ x: 393, dir: 1, P: look(22, 0, 3, '#e8c040', '#33466e', { long: 3, sleeve: 1, dk: 0.12 }) }, { x: 408, dir: -1, P: look(23, 2, 0, '#c8d0e0', '#22202c', { sleeve: 1, dk: 0.12 }) }],
  [{ x: 421, dir: 1, P: look(23, 3, 0, '#b8323a', '#22202c', { hat: '#2a2a34', sleeve: 1, dk: 0.12 }) }],
  [{ x: 461, dir: -1, P: look(22, 1, 2, '#58b0d8', '#d8d4cc', { long: 2, sleeve: 1, dk: 0.12 }) }],
].map((g, i) => ({ g, rank: rankOf(i, 0.65) }));
const L_WAITER = look(24, 4, 0, '#f4f0e8', '#1e1c24', { hat: '#f4f0e8', sleeve: 1, dk: 0.1 });
function drawSitters(t, crowd) {
  const band = inBand(t) && t > BAND.t0 + 20 && t < BAND.t1 - 10, beat = step(t, 480) & 1;
  for (const S of SITTERS) {
    if (!(S.rank < crowd)) continue;
    for (const s of S.g) {
      const eat = frac(t / 15 + s.x * 0.01) < 0.2, sip = frac(t / 20 + 0.4) < 0.18;
      let aR = 0, aL = 0;
      if (s.act === 'eat' || s.act === 'eat2') aR = eat ? 2 : 0;
      if (s.act === 'coffee') aR = sip ? 2 : 3;
      if (band) { aL = beat ? 1 : 4; if (s.act === 'hanky') aR = 1; }
      person(s.x, 264, s.P, { view: 0, sit: true, aL, aR, lean: s.act === 'eat2' && frac(t / 30) < 0.2 ? -1 : 0 });
      if (s.act === 'coffee' && !band) { R(LAST.tx + LAST.bw + 2, LAST.ty + (sip ? -3 : 1), 2, 3, '#ece8dc'); for (let k = 0; k < 2; k++) { const { u } = cycle(t, 300, k / 2); ctx.globalAlpha = 0.35 * (1 - u); dot(LAST.tx + LAST.bw + 2 + wave(t, 40, k * 0.3), LAST.ty - 1 - u * 6 + (sip ? -3 : 0), '#d8d4dc'); ctx.globalAlpha = 1; } }
      if (s.act === 'hanky' && band) hanky(t, 0.6);
    }
    if (S.g[0].act === 'eat') { R(74, 260, 4, 3, '#f4f0e6'); R(74, 260, 4, 1, '#ffffff'); dot(75, 259, '#e8d8b0'); }   // the box of beignets between them
  }
}
function drawCafePeople(t, crowd) {
  for (const G of PATRONS) {
    if (!(G.rank < crowd)) continue;
    for (const s of G.g) {
      const bite = frac(t / 12 + s.x * 0.013) < 0.18;
      person(s.x, 243, s.P, { view: 2, dir: s.dir, sit: true, aR: bite ? 5 : 3 });
      if (bite && frac(t / 12 + s.x * 0.013) > 0.1) { ctx.globalAlpha = 0.6; dot(LAST.x + s.dir * 3, LAST.hy + 2, '#ffffff'); dot(LAST.x + s.dir * 4, LAST.hy + 4, '#f0f0f0'); ctx.globalAlpha = 1; }
    }
  }
  for (const [cx, k] of [[398, 0], [430, 1], [452, 2]]) {                                     // cups and plates, steam
    R(cx, 235, 2, 1, '#f4f0e8'); R(cx + 3, 235, 3, 1, '#f8f8f0');
    if (crowd > 0) for (let j = 0; j < 2; j++) { const { u } = cycle(t, 240, j / 2 + k * 0.3); ctx.globalAlpha = 0.3 * (1 - u); dot(cx + wave(t, 30, k + j * 0.4), 233 - u * 7, '#e8e4ec'); ctx.globalAlpha = 1; }
  }
  if (crowd === 0) return;
  const s = along([[0, 414], [10, 414], [20, 470], [30, 470], [40, 414]], frac(t / 40) * 40), dir = s.moving ? (frac(t / 40) < 0.5 ? 1 : -1) : (frac(t / 40) < 0.5 ? -1 : 1);
  person(s.x, 251, L_WAITER, { view: 2, dir, step: s.moving && (Math.floor(s.x / 3) & 1), aR: 3 });
  R(LAST.x + dir * 3 - 2, LAST.ty + 1, 5, 1, '#e8e4dc'); dot(LAST.x + dir * 3, LAST.ty, '#f8f8f0');
}
/* ----- moths round the lamps ----- */
const MOTH_LAMPS = [{ x: NEAR_LAMP.x, y: NEAR_LAMP.y, r: 7, n: 6 }, ...PLAZA_LAMPS.map(L => ({ x: L.x, y: L.y, r: 5, n: 4 })), { x: 440, y: 194, r: 6, n: 4 }];
function drawMoths(t) {
  MOTH_LAMPS.forEach((L, li) => {
    for (let i = 0; i < L.n; i++) {
      const k = 48 + ((li * 7 + i * 13) % 5) * 12, a = TAU * (k * t / LOOP + i / L.n), j = hash(li * 10 + i, step(t, 2400), 81) - 0.5;
      dot(L.x + Math.cos(a) * L.r * (0.7 + 0.3 * Math.sin(a * 3)) + j, L.y + Math.sin(a) * L.r * 0.6 + j, '#f4e8d0');
    }
  });
}
/* ----- bats over the square, people out on the Pontalbas' galleries, strollers on our side of Decatur ----- */
const BATS = [[170, 104, 60, 14, 7, 11, 0.1], [300, 96, 50, 12, 9, 13, 0.5], [240, 120, 90, 10, 5, 8, 0.3], [130, 130, 40, 12, 11, 7, 0.75]];
function drawBats(t) {
  BATS.forEach(([cx, cy, ax, ay, kx, ky, ph], i) => {
    const x = Math.round(cx + ax * Math.sin(TAU * (kx * t / LOOP + ph)) + 3 * Math.sin(TAU * (31 + i) * t / LOOP)), y = Math.round(cy + ay * Math.sin(TAU * (ky * t / LOOP + ph * 2)));
    const up = (step(t, 2880) + i) & 1;
    dot(x, y, '#1a1420');
    if (up) { dot(x - 1, y - 1, '#1a1420'); dot(x + 1, y - 1, '#1a1420'); } else { dot(x - 1, y, '#1a1420'); dot(x + 1, y, '#1a1420'); dot(x - 2, y + 1, '#1a1420'); dot(x + 2, y + 1, '#1a1420'); }
  });
}
// [side, depth, gallery (1 lower, 2 upper), skin, top, act, rank]
const GALLERY = [[-1, 75, 1, 2, '#e8e4dc', 'drink', 0.12], [-1, 100, 2, 0, '#8a5ac8', 'fern', 0.58], [1, 110, 1, 1, '#2a9a8a', 'chat', 0.34], [1, 110, 1, 3, '#e8c040', 'chat2', 0.34]];
function drawGallery(t, crowd) {
  for (const [s, d, g, sk, top, act, rank] of GALLERY) {
    if (!(rank < crowd)) continue;
    const P = PON[s < 0 ? 0 : 1], k = FOC / d, x = Math.round(VPX + s * (P.lat - SET - 0.6) * k) + (act === 'chat2' ? 3 : 0), rail = Math.round(HZ + (EYE - (g === 1 ? PZ.rail1 : PZ.rail2)) * k);
    const skin = mixHex(SKINS[sk], NIGHT, 0.3), shirt = mixHex(top, NIGHT, 0.3), hy = rail - 5;
    R(x - 1, rail - 3, 3, 3, shirt); R(x - 1, hy, 2, 2, skin); dot(x - 1, hy, '#1c1412');
    if (act === 'drink' && frac(t / 20) < 0.2) { dot(x + 2, hy + 1, skin); dot(x + 2, hy, '#e8d8a8'); } else if (act === 'drink') dot(x + 2, rail - 1, '#e8d8a8');
    if (act === 'fern' && frac(t / 24 + 0.3) < 0.3) { dot(x - 2 * s, rail - 3, skin); dot(x - 3 * s, rail - 4, '#6a7a8a'); }
    if (act === 'chat' && frac(t / 10) < 0.3) dot(x + 2, rail - 3, skin);
  }
}
const NEAR_WALKERS = [
  [249, 1, 2, 0.2, [look(28, 1, 3, '#e87aa8', '#33466e', { long: 4, sleeve: 1, dk: 0.3 }), look(29, 4, 0, '#ece6da', '#22202c', { hat: '#c8a860', brim: 1, sleeve: 1, dk: 0.3 })], 'bag'],
  [250, -1, 3, 0.66, [look(29, 2, 0, '#4a9a4a', '#4a3a30', { sleeve: 1, dk: 0.3 })], 'cup'],
].map(([lane, dir, k, ph, P, carry], i) => ({ lane, dir, k, ph, P, carry, rank: rankOf(i, 0.13) }));
function drawCafeFans(t) {
  for (const [fx, i] of [[CAFE.x0 + 14, 0], [CAFE.x0 + 42, 1]]) {
    R(fx, 192, 1, 2, '#2a2020'); R(fx - 1, 194, 3, 1, '#3a2a24');
    const f = (step(t, 1920) + i) & 3;
    if (f === 0) R(fx - 4, 194, 9, 1, '#4a3a30'); else if (f === 2) R(fx - 1, 193, 3, 3, '#4a3a30'); else { dot(fx - 3 + (f === 1 ? 0 : 6), 193, '#4a3a30'); dot(fx + 3 - (f === 1 ? 0 : 6), 195, '#4a3a30'); }
  }
}
/* ----- the pedicab ----- */
const L_PEDAL = look(23, 3, 0, '#2a9a8a', '#22202c', { hat: '#2a2a34', sleeve: 1 }), L_FARE = look(22, 0, 3, '#e87aa8', '#ece6da', { long: 3, sleeve: 1 });
function pedicab(x, fy, t) {
  const st = step(t, 720) & 1;
  wheel(x - 6, fy - 4, 4, '#3a3440', x);
  R(x - 12, fy - 22, 15, 1, '#6a3a9a'); R(x - 12, fy - 21, 15, 1, '#4a2470');
  for (const px0 of [x - 12, x + 2]) for (let y = fy - 20; y < fy - 8; y++) dot(px0, y, '#1a1418');
  person(x - 5, fy - 10, L_FARE, { sit: true, view: 2, dir: 1, aR: 0 });
  R(x - 11, fy - 9, 11, 3, '#1e1c24'); R(x - 11, fy - 15, 2, 7, '#1e1c24');
  for (let k = 0; k < 16; k++) dot(x - 2 + k, fy - 6 + Math.round(k * 0.12), '#5a5a64');
  person(x + 9, fy - 2, L_PEDAL, { view: 2, dir: 1, step: st, aR: 3 });
  wheel(x + 15, fy - 4, 4, '#3a3440', x);
  halo(HL.lantern, x + 17, fy - 9, 0.7); dot(x + 17, fy - 9, '#fff4d8');
}

/* ================= The city's own weather: a fine warm drizzle that comes and goes ================= */
const DRIZ = [
  { count: 150, nMin: 150, nMax: 180, len: 2, slope: 0.1, col: '#8a7aa8', warm: '#e8c890', a: 0.32, land: [140, 172] },
  { count: 100, nMin: 190, nMax: 220, len: 3, slope: 0.13, col: '#a898c4', warm: '#ffd8a0', a: 0.42, land: [KERB + 4, NEAR - 1] },
  { count: 46, nMin: 240, nMax: 270, len: 5, slope: 0.18, col: '#c4b8dc', warm: '#ffe4b8', a: 0.5, land: [H + 6, H + 6] },
];
DRIZ.forEach((L, li) => { const r = rng(900 + li); L.drops = []; for (let i = 0; i < L.count; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r() }); });
const drizDensity = t => 0.55 + 0.3 * wave(t, 2, 0.15) + 0.15 * wave(t, 5, 0.4);
const RAIN_LIGHTS = [
  { x: NEAR_LAMP.x, y: NEAR_LAMP.y + 10, rx: 40, ry: 50 }, ...PLAZA_LAMPS.map(L => ({ x: L.x, y: L.y + 12, rx: 22, ry: 30 })),
  { x: CA.cx, y: 110, rx: 40, ry: 50, col: '#fff0dc' }, { x: 440, y: 210, rx: 34, ry: 30 }];
const RAIN_LIGHTS_BB = [...RAIN_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y + 6, rx: 30, ry: 18, col: '#fff0c8' }];
function drawDrizzle(L, t, lights) {
  const dens = drizDensity(t);
  for (const d of L.drops) {
    const { cyc, u } = cycle(t, d.n, d.ph);
    if (hash(d.id, cyc, 19) > dens) continue;
    const yl = lerp(L.land[0], L.land[1], hash(d.id, cyc, 12)), fall = yl + 10, xl = -20 + hash(d.id, cyc, 11) * (W + 20 + fall * L.slope);
    if (u < 0.9 || L.land[0] >= H) {
      const s = Math.min(1, u / 0.9), yh = yl - fall * (1 - s), xh = xl - (yl - yh) * L.slope;
      if (yh < -1 || yh > H) continue;
      let lit = null;
      for (const l of lights) { const dx = (xh - l.x) / l.rx, dy = (yh - l.y) / l.ry; if (dx * dx + dy * dy < 1) { lit = l.col || L.warm; break; } }
      ctx.fillStyle = lit || L.col; ctx.globalAlpha = lit ? L.a + 0.25 : L.a;
      for (let k = 0; k < L.len; k++) ctx.fillRect(Math.round(xh - k * L.slope), Math.round(yh - k), 1, 1);
    } else if (L.land[0] >= KERB) {                                                         // a ring on the wet street
      const s = (u - 0.9) / 0.1;
      ctx.globalAlpha = 0.35 * (1 - s); ctx.fillStyle = '#9a90b8';
      const rr = 1 + Math.round(s * 2);
      ctx.fillRect(Math.round(xl - rr), Math.round(yl), 1, 1); ctx.fillRect(Math.round(xl + rr), Math.round(yl), 1, 1);
    }
  }
  ctx.globalAlpha = 1;
}
const WEATHER = weatherPlans(['drizzle']);
const SHELTER = [[], [], [[CAFE.x0 - 12, 192, W - 1, H - 1]]];                // under the café's awning stays dry
const WX = { land: DRIZ.map(L => L.land), lights: RAIN_LIGHTS, shelter: SHELTER };
const WX_BB = { ...WX, lights: RAIN_LIGHTS_BB };

/* ================= Sprites ================= */
let CARR_C, CAR_SPR;
function buildSprites() {
  CARR_C = LIVERY.map((_, v) => renderCarriageBody(v));
  CAR_SPR = CAR_COLS.map(col => { const f = carSprite(50, 17, col), n = carSprite(60, 21, col); return { far: f, nearR: flipC(n) }; });
}

/* ================= Per-frame: the square, the plaza, the street ================= */
const CANDLES = [208, 213, 251, 258, 266, 271];      // tarot readers' candles on Chartres Street, in front of the cathedral
function drawSquare(t) {
  blit(SQUARE_C);
  for (const L of SQ_LAMPS) { dot(L.x, L.y, '#fff6e0'); dot(L.x, L.y - 1, '#ffe8c0'); halo(HL.globe, L.x, L.y, 0.9 + 0.05 * wave(t, 60, L.x * 0.01)); }
  halo(HL.statue, STATUE.x, STATUE.base - 6, 0.5);
  CANDLES.forEach((x, i) => { const f = 0.7 + 0.3 * hash(i, step(t, 1200), 4); dot(x, CA.base, f > 0.8 ? '#ffe0a0' : '#ffb860'); halo(HL.candle, x, CA.base, f * 0.7); });
}
function drawPlazaLights(t) {
  for (const L of PLAZA_LAMPS) { halo(HL.plaza, L.x, L.y + 4, 0.9); halo(HL.plazaCore, L.x, L.y, 0.9 + 0.05 * wave(t, 300, L.x)); }
  for (const gx of [GATE.x - 10, GATE.x + 10]) { dot(gx, fenceTop - 9, '#fff0c8'); halo(HL.lantern, gx, fenceTop - 9, 0.9); }
}
function plazaItems(t, crowd) {
  const items = [];
  const here = rank => crowd > 0 && rank < crowd;
  for (const C of CARRS) {
    const at = carriageAt(C, t, crowd);
    if (!at || at.fy > PARK_Y) continue;
    items.push([PARK_Y, () => carriage(C, at.x, at.fy, t, false, at.x - C.slot, { driver: crowd > 0 ? DRIVERS[C.id] : null, riders: crowd > 0 ? at.riders : [], turn: inBand(t) ? -1 : 0 })]);
  }
  if (crowd === 0) { items.push([186, () => drawPainter(t, 0)], [183, () => drawTarot(t, 0)]); return items; }
  items.push([186, () => drawTrio(t, crowd)], [186.5, () => drawPainter(t, crowd)], [183, () => drawTarot(t, crowd)]);
  const D = dancerAt(t);
  if (D && D.fy < 200) items.push([D.fy + 0.2, () => drawDancer(t, D)]);
  if (here(0.15)) items.push([185, () => drawPhoto(t)]);
  for (const Wk of WALKERS) if (here(Wk.rank)) items.push([Wk.lane, () => drawWalker(Wk, t)]);
  for (const T of TIPPERS) {
    if (!here(T.rank)) continue;
    const s = tipperAt(T, t);
    if (!s) continue;
    items.push([188.5, () => {
      person(s.x, 188, T.P, { view: 2, dir: s.dir, step: s.moving && (Math.floor(s.x / 3) & 1), bend: s.bend, aR: s.bend ? 3 : 0 });
      if (s.bend) { const g = t - T.t + 0.8; if (g > 0.2) dot(TRIO.case + T.from * 2, 186 - Math.round(Math.sin(Math.min(1, g) * Math.PI) * 2), '#ffe890'); }
    }]);
  }
  items.push(...drawBoarders(t));
  if (t >= DANCE[0] - 8 && t < DANCE[1] + 8) items.push([188.2, () => drawDanceCouple(t)]);
  return items;
}
function streetItems(t, crowd) {
  const items = [];
  for (const C of CARRS) {
    const at = carriageAt(C, t, crowd);
    if (!at || at.fy <= PARK_Y) continue;
    items.push([at.fy, () => carriage(C, at.x, at.fy, t, at.moving, at.x - C.slot, { driver: DRIVERS[C.id], riders: at.riders })]);
  }
  for (const [t0, t1, lane, ci] of CARS) {
    if (t < t0 || t >= t1) continue;
    const u = (t - t0) / (t1 - t0), far = lane < 0, spr = far ? CAR_SPR[ci].far : CAR_SPR[ci].nearR, w = spr.width, h = spr.height;
    const x = Math.round(far ? lerp(W + 10, -w - 10, u) : lerp(-w - 10, W + 10, u)), fy = far ? LANE_FAR : LANE_NEAR;
    items.push([fy, () => {
      ctx.globalAlpha = 0.22;
      for (let j = 0; j < (h >> 1); j++) ctx.drawImage(spr, 0, h - 1 - j * 2, w, 1, x, fy + 1 + j, w, 1);
      ctx.globalAlpha = 1;
      ctx.fillStyle = 'rgba(8,6,16,0.45)'; ctx.fillRect(x + 2, fy, w - 4, 1);
      ctx.drawImage(spr, x, fy - h + 1);
      const hx = far ? x : x + w - 1, tx = far ? x + w - 1 : x, ly = fy - h + 1 + Math.round(h * 0.42) + 1;
      halo(HL.car, hx, ly, 0.85); halo(HL.tail, tx, ly, 0.7);
      streak(ctx, t, hx + (far ? -6 : 6), 6, '#fff0c8', 0.22, ci * 3, fy + 2, Math.min(NEAR, fy + 18));
    }]);
  }
  if (crowd > 0 && t >= PEDICAB[0] && t < PEDICAB[1]) { const x = Math.round(lerp(-30, W + 30, (t - PEDICAB[0]) / (PEDICAB[1] - PEDICAB[0]))); items.push([LANE_NEAR - 2, () => pedicab(x, LANE_NEAR - 2, t)]); }
  if (crowd > 0) {
    items.push(...bandItems(t));
    const D = dancerAt(t);
    if (D && D.fy >= 200) items.push([D.fy + 0.2, () => drawDancer(t, D)]);
  }
  return items;
}
const STREAKS = [
  ...PLAZA_LAMPS.map((L, i) => ({ x: L.x, w: 5, col: '#ffcf88', a: 0.3, seed: i + 1 })), { x: GATE.x - 10, w: 3, col: '#ffe0a8', a: 0.2, seed: 4 },
  { x: GATE.x + 10, w: 3, col: '#ffe0a8', a: 0.2, seed: 5 }, { x: CA.cx - 6, w: 8, col: '#fff0dc', a: 0.12, seed: 6 }, { x: 420, w: 10, col: '#ffb868', a: 0.22, seed: 7 },
  { x: 160, w: 4, col: '#e0a060', a: 0.12, seed: 8 }, { x: 350, w: 4, col: '#e0a060', a: 0.12, seed: 9 }];
// The wet street mirrors what stands high over the square: seen from our steps, a puddle at row y shows the frame's row
// MIR_AXIS - y (mirrored about the cathedral's base line), so the tower and the spire hang upside down in Decatur.
const MIR_Y0 = 40, MIR_Y1 = 96, MIR_AXIS = 2 * CA.base + 1;
function snapMirror() {
  const [, x] = MIRROR;
  x.globalCompositeOperation = 'copy';
  x.drawImage(ctx.canvas, 0, MIR_Y0, W, MIR_Y1 - MIR_Y0, 0, 0, W, MIR_Y1 - MIR_Y0);
}
function drawStreet(t, crowd) {
  for (let y = KERB + 3; y < NEAR; y++) {
    const ys = MIR_AXIS - y, k = y - KERB - 3;
    if (ys < MIR_Y0 || ys >= MIR_Y1) continue;
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 0.7) * (0.6 + k * 0.05) + Math.sin(TAU * 110 * t / LOOP - k * 1.4) * 0.7);
    ctx.globalAlpha = 0.3 - k * 0.003;
    ctx.drawImage(MIRROR[0], 0, ys - MIR_Y0, W, 1, off, y, W, 1);
  }
  ctx.globalAlpha = 1;
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, KERB + 3, NEAR);
  for (const C of CARRS) { const at = carriageAt(C, t, crowd); if (at && at.fy === PARK_Y && at.x > -20 && at.x < W) streak(ctx, t, at.x + 19, 2, '#ffd890', 0.2, C.id + 20, KERB + 3, KERB + 22); }
}
function drawCafe(t, crowd, billboard, pitch) {
  for (const Wk of NEAR_WALKERS) if (crowd > 0 && Wk.rank < crowd) drawWalker(Wk, t);
  blit(CAFE_C);
  drawCafeFans(t);
  halo(HL.cafe, 440, 214, 0.9 + 0.04 * wave(t, 90));
  halo(HL.nearCore, NEAR_LAMP.x, NEAR_LAMP.y, 0.95); halo(HL.near, NEAR_LAMP.x, NEAR_LAMP.y + 6, 0.9);
  drawCafePeople(t, crowd);
  if (!billboard) return;
  const fl = (t >= 141.2 && t < 142.4) && hash(step(t, 2400), 31) < 0.5;                    // one gooseneck stutters once a loop
  halo(HL.sign, BB.x + (BB.w >> 1), BB.y + 8, fl ? 0.6 : 0.9);
  blit(BOARD_C);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(AD_C, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  halo(HL.signLamp, BB.x + 12, BB.y - 3, 0.9); if (!fl) halo(HL.signLamp, BB.x + BB.w - 9, BB.y - 3, 0.9);
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face on the café's sign; billboard: false leaves the sign out; weather: a WEATHERS pick
// (the city's own when left out); clock: New Orleans' time for the cathedral's clock; crowd: the share of the people
// to show, 0..1 (CROWD when left out or not a number), 0 = nobody
function renderFrame(t, { pitch, billboard = true, weather, clock, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, lights = billboard ? RAIN_LIGHTS_BB : RAIN_LIGHTS;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  drawSky(t);
  drawBuildings(t, clock);
  snapMirror();
  drawGallery(t, crowd);
  drawSquare(t);
  drawBats(t);
  if (wx.drizzle) drawDrizzle(DRIZ[0], t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  blit(FRONT_C);
  drawPlazaLights(t);
  const A = plazaItems(t, crowd).sort((a, b) => a[0] - b[0]);
  for (const [, fn] of A) fn();
  drawStreet(t, crowd);
  const B = streetItems(t, crowd).sort((a, b) => a[0] - b[0]);
  for (const [, fn] of B) fn();
  if (wx.drizzle) drawDrizzle(DRIZ[1], t, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawCafe(t, crowd, billboard, pitch);
  drawSitters(t, crowd);
  drawMoths(t);
  if (wx.drizzle) drawDrizzle(DRIZ[2], t, lights);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'new-orleans',
  name: 'New Orleans',
  tagline: 'Jazz on Jackson Square',
  timeZone: 'America/Chicago',
  accent: '#7f00ff',                                   // written out: tools/seo/seo.mjs reads it from the source
  focusX: CA.cx,
  landmark: [CAB.x0, PRE.x1],                          // the Cabildo, St Louis Cathedral and the Presbytère
  landmarkTop: CAY.cross,                              // the row of the cross on the middle spire
  billboard: BB,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  ambience: { traffic: 0.14, crowd: 0.14, crickets: 0.16, rain: 0.12 },
  events: [
    ...BAND.tuba.map((tt, i) => ({ t: tt, type: 'horn', x: 10 + i * 12, gain: 0.14 + i * 0.06 })),
    ...[[92, 150], [106, 290], [120, 410], [133, 470]].map(([tt, x]) => ({ t: tt, type: 'horn', x, gain: 0.3 })),
    { t: CHIME, type: 'chime', x: CA.cx, gain: 0.3 },
    ...BELLS.map(tt => ({ t: tt, type: 'bell', x: CA.cx, gain: 0.5 })),
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
